import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Descriptions,
  Tabs,
  message,
  Statistic,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Table,
  Popconfirm,
  Alert,
  Timeline,
  Empty,
  Progress,
  Tooltip,
  Collapse,
} from 'antd';
import {
  GiftOutlined,
  StopOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  MinusOutlined,
  SwapOutlined,
  MergeCellsOutlined,
  TrophyOutlined,
  TeamOutlined,
  DeleteOutlined,
  FireOutlined,
  RiseOutlined,
  UndoOutlined,
  DashboardOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '@/services/api';
import type {
  Player,
  BanHistory,
  PlayerEconomy,
  CurrencyTransaction,
  BanReason,
  AuthProvider,
  TransferHistory,
  PlayerMatchHistory,
  PlayerMatch,
  PlayerSquadsResponse,
  PlayerSquad,
  PlayerSquadSlot,
  MatchdayDirectorData,
  UpdateMatchdayDirectorRequest,
  PlayerDevicesResponse,
  PlayerDevice,
  PlayerCard,
  PlayerCardsResponse,
  Purchase,
  UserPurchaseStats,
} from '@/types';
import { PlayerAiAnalytics } from '@/components/PlayerAiAnalytics';
import { PlayerInstallSource } from '@/components/PlayerInstallSource';
import { SquadSlotEditor } from './SquadSlotEditor';
import { ExportButton } from '@components/ExportButton';
import { formatMoney } from '@utils/formatMoney';

interface BanFormValues {
  banType: 'temporary' | 'permanent';
  reason: BanReason;
  reasonDetails?: string;
  durationDays?: number;
}

interface UnbanFormValues {
  revocationReason: string;
}

interface GrantFormValues {
  currencyType: string;
  amount: number;
  isDeduction: boolean;
  reason: string;
}

interface EditEconomyFormValues {
  coins?: number;
  evoTokens?: number;
  matchTokens?: number;
  premiumCurrency?: number;
  fanRating?: number;
  totalXp?: number;
  level?: number;
  reason: string;
}

interface TransferFormValues {
  targetProvider: AuthProvider;
  googlePlayGamesId?: string;
  appleGameCenterId?: string;
  deviceId?: string;
  reason: string;
  clearOldPlatformIds?: boolean;
}

interface EditDirectorFormValues {
  currentDifficulty?: number;
  suggestedDifficulty?: number;
  churnRiskScore?: number;
  consecutiveLosses?: number;
  daysSinceLastMatch?: number;
  winRate?: number;
  recentWinRate?: number;
  reason: string;
}

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Division presets for Fan Rating selection
const DIVISION_PRESETS = [
  { name: 'Rookie League III', minRating: 0 },
  { name: 'Rookie League II', minRating: 500 },
  { name: 'Rookie League I', minRating: 1000 },
  { name: 'Semi-Pro III', minRating: 1500 },
  { name: 'Semi-Pro II', minRating: 2000 },
  { name: 'Semi-Pro I', minRating: 2500 },
  { name: 'Pro III', minRating: 3000 },
  { name: 'Pro II', minRating: 3500 },
  { name: 'Pro I', minRating: 4000 },
  { name: 'National III', minRating: 4500 },
  { name: 'National II', minRating: 5000 },
  { name: 'National I', minRating: 5500 },
  { name: 'Continental III', minRating: 6000 },
  { name: 'Continental II', minRating: 6500 },
  { name: 'Continental I', minRating: 7000 },
  { name: 'Global III', minRating: 7500 },
  { name: 'Global II', minRating: 8000 },
  { name: 'Global I', minRating: 8500 },
  { name: 'Legendary III', minRating: 9000 },
  { name: 'Legendary II', minRating: 9500 },
  { name: 'Legendary I', minRating: 10000 },
];

const getDivisionName = (rating: number): string => {
  for (let i = DIVISION_PRESETS.length - 1; i >= 0; i--) {
    if (rating >= DIVISION_PRESETS[i].minRating) return DIVISION_PRESETS[i].name;
  }
  return 'Rookie League III';
};

const BAN_REASONS: { value: BanReason; label: string }[] = [
  { value: 'cheating', label: 'Cheating' },
  { value: 'hacking', label: 'Hacking' },
  { value: 'abusive_behavior', label: 'Abusive Behavior' },
  { value: 'fraud', label: 'Fraud' },
  { value: 'multiple_accounts', label: 'Multiple Accounts' },
  { value: 'terms_violation', label: 'Terms Violation' },
  { value: 'suspicious_activity', label: 'Suspicious Activity' },
  { value: 'refund_abuse', label: 'Refund Abuse' },
  { value: 'other', label: 'Other' },
];

const CURRENCY_TYPES = [
  { value: 'coins', label: 'Coins' },
  { value: 'evoTokens', label: 'Evo Tokens' },
  { value: 'matchTokens', label: 'Match Tokens' },
  { value: 'premiumCurrency', label: 'Premium Currency (Gems)' },
  { value: 'fanRating', label: 'Fan Rating' },
];

const AUTH_PROVIDERS: { value: AuthProvider; label: string }[] = [
  { value: 'guest' as AuthProvider, label: 'Guest (Device)' },
  { value: 'google_play_games' as AuthProvider, label: 'Google Play Games' },
  { value: 'apple_game_center' as AuthProvider, label: 'Apple Game Center' },
];

export const PlayerShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [player, setPlayer] = useState<Player | null>(null);
  const [economy, setEconomy] = useState<PlayerEconomy | null>(null);
  const [banHistory, setBanHistory] = useState<BanHistory[]>([]);
  const [transactions, setTransactions] = useState<CurrencyTransaction[]>([]);
  const [txMeta, setTxMeta] = useState<{ total: number; page: number; totalPages: number }>({
    total: 0,
    page: 1,
    totalPages: 1,
  });
  const [txFilters, setTxFilters] = useState<{
    currencyType?: string;
    source?: string;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
  }>({});
  const [txLoading, setTxLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  // New feature states
  const [transferHistory, setTransferHistory] = useState<TransferHistory[]>([]);
  const [matchHistory, setMatchHistory] = useState<PlayerMatchHistory | null>(null);
  const [squads, setSquads] = useState<PlayerSquadsResponse | null>(null);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [squadsLoading, setSquadsLoading] = useState(false);
  const [directorData, setDirectorData] = useState<MatchdayDirectorData | null>(null);
  const [directorLoading, setDirectorLoading] = useState(false);
  const [devices, setDevices] = useState<PlayerDevicesResponse | null>(null);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [playerCards, setPlayerCards] = useState<PlayerCardsResponse | null>(null);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [editingSlot, setEditingSlot] = useState<{
    squadType: 'home' | 'away';
    position: string;
    slot?: PlayerSquadSlot;
  } | null>(null);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [purchaseStats, setPurchaseStats] = useState<UserPurchaseStats | null>(null);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [grantPurchaseModalVisible, setGrantPurchaseModalVisible] = useState(false);
  const [productList, setProductList] = useState<
    Array<{
      id: string;
      name: string;
      storeProductId: string;
      items: Record<string, number>;
      type: string;
    }>
  >([]);
  const [grantPurchaseForm] = Form.useForm();

  // Modal states
  const [banModalVisible, setBanModalVisible] = useState(false);
  const [unbanModalVisible, setUnbanModalVisible] = useState(false);
  const [grantModalVisible, setGrantModalVisible] = useState(false);
  const [editEconomyModalVisible, setEditEconomyModalVisible] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [setDivisionModalVisible, setSetDivisionModalVisible] = useState(false);
  const [editDirectorModalVisible, setEditDirectorModalVisible] = useState(false);
  const [gastOverrideModalVisible, setGastOverrideModalVisible] = useState(false);
  const [aiAnalyticsModalVisible, setAiAnalyticsModalVisible] = useState(false);
  const [matchPage, setMatchPage] = useState(1);
  const [matchPageSize, setMatchPageSize] = useState(20);
  const navigate = useNavigate();

  // Form instances
  const [banForm] = Form.useForm();
  const [unbanForm] = Form.useForm();
  const [grantForm] = Form.useForm();
  const [editEconomyForm] = Form.useForm();
  const [transferForm] = Form.useForm();
  const [mergeModalVisible, setMergeModalVisible] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeForm] = Form.useForm();
  const [setDivisionForm] = Form.useForm();
  const [editDirectorForm] = Form.useForm();
  const [gastOverrideForm] = Form.useForm();

  const fetchPlayer = useCallback(async () => {
    try {
      const response = await adminApi.getPlayer(id!);
      setPlayer(response.data);
    } catch (error) {
      console.error(error);
      message.error('Failed to load player');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchEconomy = useCallback(async () => {
    try {
      const response = await adminApi.getPlayerEconomy(id!);
      setEconomy(response.data);
    } catch (error) {
      console.error('Failed to load economy:', error);
    }
  }, [id]);

  const fetchBanHistory = useCallback(async () => {
    try {
      const response = await adminApi.getBanHistory(id!);
      setBanHistory(response.data || []);
    } catch (error) {
      console.error('Failed to load ban history:', error);
    }
  }, [id]);

  const fetchTransactions = useCallback(
    async (filters?: typeof txFilters) => {
      if (!id) return;
      setTxLoading(true);
      try {
        const params: Record<string, string | number | boolean> = { limit: 50 };
        const f = filters ?? txFilters;
        if (f.currencyType) params.currencyType = f.currencyType;
        if (f.source) params.source = f.source;
        if (f.sortBy) params.sortBy = f.sortBy;
        if (f.sortOrder) params.sortOrder = f.sortOrder;
        if (f.page) params.page = f.page;
        const response = await adminApi.getCurrencyTransactions(id, params);
        setTransactions(response.data?.data || []);
        if (response.data?.meta) setTxMeta(response.data.meta);
      } catch (error) {
        console.error('Failed to load transactions:', error);
      } finally {
        setTxLoading(false);
      }
    },
    [id, txFilters]
  );

  const fetchTransferHistory = useCallback(async () => {
    try {
      const response = await adminApi.getTransferHistory(id!);
      setTransferHistory(response.data || []);
    } catch (error) {
      console.error('Failed to load transfer history:', error);
    }
  }, [id]);

  const fetchMatchHistory = useCallback(
    async (page = 1, pageSize = 20) => {
      setMatchesLoading(true);
      try {
        const offset = (page - 1) * pageSize;
        const response = await adminApi.getPlayerMatches(id!, {
          limit: pageSize,
          offset,
        });
        setMatchHistory(response.data);
      } catch (error) {
        console.error('Failed to load match history:', error);
      } finally {
        setMatchesLoading(false);
      }
    },
    [id]
  );

  const fetchSquads = useCallback(async () => {
    setSquadsLoading(true);
    try {
      const response = await adminApi.getPlayerSquads(id!);
      setSquads(response.data);
    } catch (error) {
      console.error('Failed to load squads:', error);
    } finally {
      setSquadsLoading(false);
    }
  }, [id]);

  const fetchDirectorData = useCallback(async () => {
    setDirectorLoading(true);
    try {
      const response = await adminApi.getPlayerMatchdayDirector(id!);
      setDirectorData(response.data);
    } catch (error) {
      console.error('Failed to load matchday director data:', error);
    } finally {
      setDirectorLoading(false);
    }
  }, [id]);

  const fetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const response = await adminApi.getPlayerDevices(id!);
      setDevices(response.data);
    } catch (error) {
      console.error('Failed to load devices:', error);
    } finally {
      setDevicesLoading(false);
    }
  }, [id]);

  const fetchPlayerCards = useCallback(async () => {
    setCardsLoading(true);
    try {
      const response = await adminApi.getPlayerCards(id!);
      setPlayerCards(response.data);
    } catch (error) {
      console.error('Failed to load player cards:', error);
    } finally {
      setCardsLoading(false);
    }
  }, [id]);

  const fetchPurchases = useCallback(async () => {
    if (!id) return;
    setPurchasesLoading(true);
    try {
      const [purchasesRes, statsRes] = await Promise.all([
        adminApi.getUserPurchases(id, { limit: 100 }),
        adminApi.getUserPurchaseStats(id),
      ]);
      setPurchases(purchasesRes.data?.purchases || purchasesRes.data?.data || []);
      setPurchaseStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load purchases:', error);
    } finally {
      setPurchasesLoading(false);
    }
  }, [id]);

  const loadProducts = useCallback(async () => {
    try {
      const res = await adminApi.getStoreSyncProducts({ limit: 200 });
      const prods = res.data?.items || res.data?.products || res.data?.data || [];
      setProductList(
        prods.map((p: Record<string, unknown>) => ({
          id: (p.id as string) || '',
          name: (p.name as string) || '',
          storeProductId: (p.storeProductId as string) || '',
          items: (p.items || {}) as Record<string, number>,
          type: (p.category as string) || (p.type as string) || '',
        }))
      );
    } catch {
      /* ignore */
    }
  }, []);

  const handleGrantPurchase = async (values: {
    productId: string;
    orderId?: string;
    reason: string;
  }) => {
    try {
      const result = await adminApi.grantPurchase({
        userId: id!,
        productId: values.productId,
        orderId: values.orderId,
        reason: values.reason,
      });
      message.success(
        `Purchase granted! Items: ${JSON.stringify(result.data?.grantedItems || {})}`
      );
      setGrantPurchaseModalVisible(false);
      grantPurchaseForm.resetFields();
      void Promise.all([
        fetchPurchases(),
        fetchEconomy(),
        fetchPlayerCards(),
        fetchTransactions(),
        fetchSquads(),
      ]);
    } catch (error) {
      message.error('Failed to grant purchase');
    }
  };

  useEffect(() => {
    if (id) {
      fetchPlayer();
      fetchEconomy();
      fetchBanHistory();
      fetchTransactions();
      fetchTransferHistory();
      fetchMatchHistory();
      fetchSquads();
      fetchDirectorData();
      fetchDevices();
      fetchPlayerCards();
      fetchPurchases();
    }
  }, [
    id,
    fetchPlayer,
    fetchEconomy,
    fetchBanHistory,
    fetchTransactions,
    fetchTransferHistory,
    fetchMatchHistory,
    fetchSquads,
    fetchDirectorData,
    fetchDevices,
    fetchPlayerCards,
    fetchPurchases,
  ]);

  const handleBanPlayer = async (values: BanFormValues) => {
    try {
      await adminApi.banPlayer(id!, {
        banType: values.banType,
        reason: values.reason,
        reasonDetails: values.reasonDetails,
        durationDays: values.banType === 'temporary' ? values.durationDays : undefined,
      });
      message.success('Player banned successfully');
      setBanModalVisible(false);
      banForm.resetFields();
      fetchPlayer();
      fetchBanHistory();
    } catch (error) {
      message.error('Failed to ban player');
    }
  };

  const handleUnbanPlayer = async (values: UnbanFormValues) => {
    try {
      await adminApi.unbanPlayer(id!, {
        revocationReason: values.revocationReason,
      });
      message.success('Player unbanned successfully');
      setUnbanModalVisible(false);
      unbanForm.resetFields();
      fetchPlayer();
      fetchBanHistory();
    } catch (error) {
      message.error('Failed to unban player');
    }
  };

  const handleGrantCurrency = async (values: GrantFormValues) => {
    try {
      await adminApi.grantCurrency(id!, {
        currencyType: values.currencyType,
        amount: values.isDeduction ? -Math.abs(values.amount) : Math.abs(values.amount),
        reason: values.reason,
      });
      message.success(`Currency ${values.isDeduction ? 'deducted' : 'granted'} successfully`);
      setGrantModalVisible(false);
      grantForm.resetFields();
      fetchEconomy();
      fetchTransactions();
    } catch (error) {
      message.error('Failed to update currency');
    }
  };

  const handleEditEconomy = async (values: EditEconomyFormValues) => {
    try {
      await adminApi.updatePlayerEconomy(id!, {
        ...values,
        reason: values.reason,
      });
      message.success('Economy updated successfully');
      setEditEconomyModalVisible(false);
      editEconomyForm.resetFields();
      fetchEconomy();
      fetchTransactions();
    } catch (error) {
      message.error('Failed to update economy');
    }
  };

  const handleEditDirector = async (values: EditDirectorFormValues) => {
    try {
      const payload: UpdateMatchdayDirectorRequest = { reason: values.reason };
      if (values.currentDifficulty !== undefined)
        payload.currentDifficulty = values.currentDifficulty;
      if (values.suggestedDifficulty !== undefined)
        payload.suggestedDifficulty = values.suggestedDifficulty;
      if (values.churnRiskScore !== undefined) payload.churnRiskScore = values.churnRiskScore;
      if (values.consecutiveLosses !== undefined)
        payload.consecutiveLosses = values.consecutiveLosses;
      if (values.daysSinceLastMatch !== undefined)
        payload.daysSinceLastMatch = values.daysSinceLastMatch;
      if (values.winRate !== undefined) payload.winRate = values.winRate;
      if (values.recentWinRate !== undefined) payload.recentWinRate = values.recentWinRate;
      await adminApi.updatePlayerMatchdayDirector(id!, payload);
      message.success('Matchday Director updated successfully');
      setEditDirectorModalVisible(false);
      editDirectorForm.resetFields();
      fetchDirectorData();
    } catch (error) {
      message.error('Failed to update Matchday Director');
    }
  };

  const handleGastOverride = async (values: { value: number | null; reason: string }) => {
    try {
      await adminApi.setGASTOverride(values);
      message.success(
        values.value !== null ? 'gAST override set successfully' : 'gAST override cleared'
      );
      setGastOverrideModalVisible(false);
      gastOverrideForm.resetFields();
      fetchDirectorData();
    } catch (error) {
      message.error('Failed to update gAST');
    }
  };

  const handleTransferAccount = async (values: TransferFormValues) => {
    setTransferLoading(true);
    try {
      await adminApi.transferAccount(id!, {
        targetProvider: values.targetProvider,
        googlePlayGamesId: values.googlePlayGamesId,
        appleGameCenterId: values.appleGameCenterId,
        deviceId: values.deviceId,
        reason: values.reason,
        clearOldPlatformIds: values.clearOldPlatformIds,
      });
      message.success('Account transferred successfully');
      setTransferModalVisible(false);
      transferForm.resetFields();
      fetchPlayer();
      fetchTransferHistory();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to transfer account';
      message.error(errorMessage);
    } finally {
      setTransferLoading(false);
    }
  };

  const handleMergeAccount = async (values: { loserUserId: string; reason?: string }) => {
    setMergeLoading(true);
    try {
      const res = await adminApi.mergeAccount(id!, {
        loserUserId: values.loserUserId,
        reason: values.reason,
      });
      message.success(
        `Merged ${res.data?.loserId ?? 'account'} into this account (${
          res.data?.tablesTouched ?? 0
        } tables re-parented)`
      );
      setMergeModalVisible(false);
      mergeForm.resetFields();
      fetchPlayer();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to merge account';
      message.error(errorMessage);
    } finally {
      setMergeLoading(false);
    }
  };

  const toggleStatus = async () => {
    if (!player) return;
    try {
      const newStatus = !player.isActive;
      await adminApi.updatePlayerStatus(player.id, newStatus);
      message.success(`Player ${newStatus ? 'activated' : 'deactivated'} successfully`);
      fetchPlayer();
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  const handleDeletePlayer = async () => {
    if (!player) return;
    if (!deletePassword) {
      message.error('Please enter your admin password to confirm');
      return;
    }
    setDeleteLoading(true);
    try {
      const response = await adminApi.deletePlayer(player.id, deletePassword);
      const result = response.data;
      message.success(
        `Player deleted successfully. Removed data from ${result.deletedTables?.length || 0} tables.`
      );
      setDeleteModalVisible(false);
      // Navigate back to player list after deletion
      navigate('/players');
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to delete player data';
      message.error(errorMessage || 'Failed to delete player data');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCompleteStump = async () => {
    try {
      await adminApi.completeStumpRewards(id!);
      message.success('All stump tasks marked as completed');
    } catch (error) {
      message.error('Failed to complete stump tasks');
    }
  };

  const handleResetStump = async () => {
    try {
      await adminApi.resetStumpRewards(id!);
      message.success('Stump rewards reset for today');
    } catch (error) {
      message.error('Failed to reset stump rewards');
    }
  };

  const handleSetDivision = async (values: { fanRating: number }) => {
    try {
      const response = await adminApi.setPlayerDivision(id!, values.fanRating);
      message.success(
        `Division set to ${response.data.divisionName} (rating: ${response.data.fanRating})`
      );
      setSetDivisionModalVisible(false);
      setDivisionForm.resetFields();
      fetchEconomy();
    } catch (error) {
      message.error('Failed to set division');
    }
  };

  if (loading) {
    return <Card loading />;
  }

  if (!player) {
    return <Card>Player not found</Card>;
  }

  const activeBan = banHistory.find((b) => b.status === 'active');

  const sourceColorMap: Record<string, string> = {
    admin: 'purple',
    match: 'green',
    shop: 'blue',
    level: 'gold',
    stump: 'orange',
    daily: 'cyan',
    transfer: 'magenta',
  };
  const getSourceColor = (source: string) => {
    const key = Object.keys(sourceColorMap).find((k) => source.toLowerCase().includes(k));
    return key ? sourceColorMap[key] : 'default';
  };

  const transactionColumns = [
    {
      title: 'Type',
      dataIndex: 'currencyType',
      key: 'currencyType',
      width: 120,
      filters: [
        { text: 'Coins', value: 'coins' },
        { text: 'Evo Tokens', value: 'evoTokens' },
        { text: 'Match Tokens', value: 'matchTokens' },
        { text: 'Premium', value: 'premiumCurrency' },
      ],
      onFilter: (value: unknown, record: CurrencyTransaction) => record.currencyType === value,
      render: (type: string) => {
        const colors: Record<string, string> = {
          coins: 'gold',
          evoTokens: 'purple',
          matchTokens: 'blue',
          premiumCurrency: 'magenta',
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      sorter: (a: CurrencyTransaction, b: CurrencyTransaction) => a.amount - b.amount,
      render: (amount: number) => (
        <Text strong style={{ color: amount >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {amount >= 0 ? '+' : ''}
          {amount.toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 180,
      ellipsis: true,
      render: (source: string) => <Tag color={getSourceColor(source)}>{source}</Tag>,
    },
    {
      title: 'Balance',
      key: 'balance',
      width: 160,
      render: (_: unknown, record: CurrencyTransaction) => (
        <Text type="secondary">
          {record.previousBalance.toLocaleString()} →{' '}
          <Text strong>{record.newBalance.toLocaleString()}</Text>
        </Text>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      sorter: (a: CurrencyTransaction, b: CurrencyTransaction) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      defaultSortOrder: 'descend' as const,
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  const matchColumns = [
    {
      title: 'Match Type',
      dataIndex: 'matchType',
      key: 'matchType',
      render: (type: string) => <Tag>{type?.replace('_', ' ').toUpperCase()}</Tag>,
    },
    {
      title: 'Opponent',
      dataIndex: 'opponentName',
      key: 'opponentName',
      render: (value: string | null, record: PlayerMatch) => {
        const a = record.aiAnalytics;
        // Custom matches (tour=1): client tells us which team it actually
        // played against via aiteam — prefer that over the generic
        // opponentName from the match config.
        if (a?.tour === 1 && a.aiteam) return a.aiteam;
        return value || '-';
      },
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
      render: (result: string) => (
        <Tag color={result === 'win' ? 'green' : result === 'loss' ? 'red' : 'orange'}>
          {result?.toUpperCase() || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'DF',
      key: 'df',
      render: (_: unknown, record: PlayerMatch) => {
        if (record.dF == null) return <span style={{ color: '#999' }}>-</span>;
        const tierColor: Record<string, string> = {
          very_easy: 'default',
          easy: 'green',
          medium: 'blue',
          hard: 'orange',
          very_hard: 'red',
        };
        return (
          <Space size="small">
            <strong>{record.dF.toFixed(3)}</strong>
            {record.dFLevel && (
              <Tag color={tierColor[record.dFLevel] || 'default'}>
                {record.dFLevel.replace('_', ' ').toUpperCase()}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Score',
      key: 'score',
      render: (_: unknown, record: PlayerMatch) => (
        <span>
          {record.userRuns}/{record.userWickets} vs {record.opponentRuns}/{record.opponentWickets}
        </span>
      ),
    },
    {
      title: 'Rewards',
      key: 'rewards',
      render: (_: unknown, record: PlayerMatch) => (
        <Space size="small">
          {record.xpEarned && <Tag color="purple">+{record.xpEarned} XP</Tag>}
          {record.coinsEarned && <Tag color="gold">+{record.coinsEarned} Coins</Tag>}
        </Space>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'completedAt',
      key: 'completedAt',
      render: (date: string) => (date ? new Date(date).toLocaleString() : '-'),
    },
  ];

  const rarityMap: Record<number, string> = {
    0: 'common',
    1: 'rare',
    2: 'epic',
    3: 'legendary',
  };
  const rarityColors: Record<string, string> = {
    legendary: 'gold',
    epic: 'purple',
    rare: 'blue',
    common: 'default',
  };
  const typeLabels: Record<number, string> = {
    0: 'Batsman',
    1: 'Bowler',
    2: 'All-Rounder',
    3: 'WK-Batsman',
  };
  const renderRarity = (rarity: number) => {
    const label = rarityMap[rarity] || 'common';
    return <Tag color={rarityColors[label]}>{label.toUpperCase()}</Tag>;
  };

  const makeSquadSlotColumns = (squadType: 'home' | 'away') => [
    {
      title: 'Pos',
      dataIndex: 'position',
      key: 'position',
      width: 110,
      fixed: 'left' as const,
      render: (pos: string) => <Tag>{pos?.replace(/_/g, ' ').toUpperCase()}</Tag>,
    },
    {
      title: 'Player',
      key: 'player',
      width: 180,
      render: (_: unknown, slot: PlayerSquadSlot) =>
        slot.firstName ? (
          <Space size={4}>
            <Text strong>
              {slot.firstName} {slot.secondName}
            </Text>
            {slot.role === 'captain' && <Tag color="gold">C</Tag>}
            {slot.role === 'vice_captain' && <Tag color="silver">VC</Tag>}
          </Space>
        ) : (
          <Text type="secondary" italic>
            Empty
          </Text>
        ),
    },
    {
      title: 'Type',
      dataIndex: 'playerType',
      key: 'playerType',
      width: 90,
      render: (t: number) => typeLabels[t] || '-',
    },
    {
      title: 'Rarity',
      dataIndex: 'rarity',
      key: 'rarity',
      width: 100,
      render: (r: number) => (r != null ? renderRarity(r) : null),
    },
    { title: 'Rating', dataIndex: 'overallRating', key: 'overallRating', width: 70 },
    { title: 'Lvl', dataIndex: 'playerLevel', key: 'playerLevel', width: 50 },
    { title: 'Evo', dataIndex: 'evolutionLevel', key: 'evolutionLevel', width: 50 },
    { title: 'BAT', dataIndex: 'battingAggregate', key: 'bat', width: 50 },
    { title: 'BWL', dataIndex: 'bowlingAggregate', key: 'bwl', width: 50 },
    { title: 'FLD', dataIndex: 'fieldingAggregate', key: 'fld', width: 50 },
    { title: 'PHY', dataIndex: 'physicalAggregate', key: 'phy', width: 50 },
    {
      title: 'WK',
      dataIndex: 'isWicketKeeper',
      key: 'wk',
      width: 50,
      render: (v: boolean) => (v ? <Tag color="cyan">WK</Tag> : null),
    },
    {
      title: 'Action',
      key: 'action',
      width: 110,
      fixed: 'right' as const,
      render: (_: unknown, slot: PlayerSquadSlot) => {
        const filled = !!slot.playerCardId;
        return (
          <Button
            size="small"
            type={filled ? 'default' : 'primary'}
            ghost={!filled}
            onClick={() => setEditingSlot({ squadType, position: slot.position, slot })}
          >
            {filled ? 'Edit' : 'Assign'}
          </Button>
        );
      },
    },
  ];

  const renderSquadCard = (squad: PlayerSquad) => (
    <Collapse
      key={squad.id}
      style={{ marginBottom: 16 }}
      defaultActiveKey={[]}
      items={[
        {
          key: squad.id,
          label: (
            <Space>
              <TeamOutlined />
              <Text strong>{squad.name || `${squad.squadType.toUpperCase()} Squad`}</Text>
              <Tag>{squad.squadType.toUpperCase()}</Tag>
              <Tag color="blue">Rating: {squad.totalRating || 0}</Tag>
              <Tag>{squad.playerCount || 0}/15 players</Tag>
            </Space>
          ),
          children: (
            <Table
              dataSource={squad.slots || []}
              columns={makeSquadSlotColumns(squad.squadType)}
              rowKey="position"
              pagination={false}
              size="small"
              scroll={{ x: 1100 }}
              expandable={{
                expandedRowRender: (slot: PlayerSquadSlot) => (
                  <Row gutter={24}>
                    <Col span={12}>
                      <Descriptions title="Batting" size="small" column={2} bordered>
                        <Descriptions.Item label="Runs">
                          {slot.battingStats?.totalRuns ?? 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="Balls">
                          {slot.battingStats?.totalBallsFaced ?? 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="4s">
                          {slot.battingStats?.totalFours ?? 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="6s">
                          {slot.battingStats?.totalSixes ?? 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="50s">
                          {slot.battingStats?.halfCenturies ?? 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="100s">
                          {slot.battingStats?.centuries ?? 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="Best">
                          {slot.battingStats?.bestScore ?? 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="Inn">
                          {slot.battingStats?.inningsPlayed ?? 0}
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                    <Col span={12}>
                      <Descriptions title="Bowling" size="small" column={2} bordered>
                        <Descriptions.Item label="Wkts">
                          {slot.bowlingStats?.totalWickets ?? 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="Balls">
                          {slot.bowlingStats?.totalBallsBowled ?? 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="Runs">
                          {slot.bowlingStats?.totalRunsGiven ?? 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="Maidens">
                          {slot.bowlingStats?.totalMaidens ?? 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="Best">
                          {slot.bowlingStats?.bestBowlingWickets ?? 0}W
                        </Descriptions.Item>
                        <Descriptions.Item label="5W">
                          {slot.bowlingStats?.fiveWicketHauls ?? 0}
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                  </Row>
                ),
                rowExpandable: (slot: PlayerSquadSlot) => !!slot.playerCardId,
              }}
            />
          ),
        },
      ]}
    />
  );

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <Space>
          <Title level={3} style={{ marginBottom: 0 }}>
            {player.displayName || 'Unknown Player'}
          </Title>
          <Tag color={player.isActive ? 'success' : 'error'}>
            {player.isActive ? 'ACTIVE' : 'INACTIVE'}
          </Tag>
          {activeBan && <Tag color="red">BANNED</Tag>}
        </Space>
        <div>
          <Text type="secondary">Player ID: {player.id}</Text>
        </div>
      </div>

      {/* Active Ban Alert */}
      {activeBan && (
        <Alert
          message="Player is Currently Banned"
          description={
            <div>
              <p>
                <strong>Reason:</strong> {activeBan.reason} -{' '}
                {activeBan.reasonDetails || 'No details'}
              </p>
              <p>
                <strong>Type:</strong>{' '}
                {activeBan.banType === 'permanent'
                  ? 'Permanent'
                  : `Temporary (${activeBan.durationDays} days)`}
              </p>
              {activeBan.expiresAt && (
                <p>
                  <strong>Expires:</strong> {new Date(activeBan.expiresAt).toLocaleString()}
                </p>
              )}
              <p>
                <strong>Banned by:</strong> {activeBan.bannedByAdminEmail}
              </p>
            </div>
          }
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 24 }}
          action={
            <Button danger onClick={() => setUnbanModalVisible(true)}>
              Unban Player
            </Button>
          }
        />
      )}

      {/* Quick Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card>
            <Statistic title="Level" value={economy?.level || player.level || 0} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="Fan Rating" value={economy?.fanRating || player.rating || 0} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="Coins" value={economy?.coins || player.coins || 0} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="Premium Currency" value={economy?.premiumCurrency || 0} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="Login Count" value={player.loginCount || 0} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="Platform" value={player.platform || 'N/A'} />
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Card style={{ marginBottom: 24 }}>
        <Space wrap>
          <Button icon={<GiftOutlined />} onClick={() => setGrantModalVisible(true)}>
            Grant/Deduct Currency
          </Button>
          <Button
            icon={<DollarOutlined />}
            onClick={() => {
              editEconomyForm.setFieldsValue({
                coins: economy?.coins,
                evoTokens: economy?.evoTokens,
                matchTokens: economy?.matchTokens,
                premiumCurrency: economy?.premiumCurrency,
                fanRating: economy?.fanRating,
                totalXp: economy?.totalXp,
                level: economy?.level,
              });
              setEditEconomyModalVisible(true);
            }}
          >
            Edit Economy
          </Button>
          <Button icon={<SwapOutlined />} onClick={() => setTransferModalVisible(true)}>
            Transfer Account
          </Button>
          <Button icon={<MergeCellsOutlined />} onClick={() => setMergeModalVisible(true)}>
            Merge Account
          </Button>
          {!activeBan ? (
            <Button danger icon={<StopOutlined />} onClick={() => setBanModalVisible(true)}>
              Ban Player
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => setUnbanModalVisible(true)}
            >
              Unban Player
            </Button>
          )}
          {player.isActive ? (
            <Popconfirm title="Deactivate player?" onConfirm={toggleStatus}>
              <Button danger>Deactivate</Button>
            </Popconfirm>
          ) : (
            <Popconfirm title="Activate player?" onConfirm={toggleStatus}>
              <Button type="primary">Activate</Button>
            </Popconfirm>
          )}
          <Button danger icon={<DeleteOutlined />} onClick={() => setDeleteModalVisible(true)}>
            Delete Player
          </Button>
          <Popconfirm title="Complete all stump daily tasks?" onConfirm={handleCompleteStump}>
            <Button icon={<FireOutlined />}>Complete Stump</Button>
          </Popconfirm>
          <Popconfirm title="Reset stump rewards for today?" onConfirm={handleResetStump}>
            <Button icon={<UndoOutlined />}>Reset Stump</Button>
          </Popconfirm>
          <Button icon={<RiseOutlined />} onClick={() => setSetDivisionModalVisible(true)}>
            Set Division
          </Button>
          <Popconfirm
            title="Fill empty squad slots from collection?"
            description={
              <div style={{ maxWidth: 360 }}>
                Fills every empty home-squad slot — XI (strict role match), subs (any non-WK), and
                extras (any leftover including spare WK) — using the user&apos;s existing unused
                cards only. No new cards are generated. Slots with no eligible card are left empty.
              </div>
            }
            okText="Fill"
            onConfirm={async () => {
              try {
                const res = await adminApi.fillHomeSquadFromCollection(id!);
                const { filled, byCategory, finalPlayerCount, finalTotalRating } = res.data;
                if (filled === 0) {
                  message.info('Squad already filled — no empty slots eligible.');
                } else {
                  message.success(
                    `Filled ${filled} slot${filled === 1 ? '' : 's'} ` +
                      `(XI ${byCategory.xi} · Subs ${byCategory.subs} · Extras ${byCategory.extras}). ` +
                      `Squad now ${finalPlayerCount} cards, rating ${finalTotalRating}.`
                  );
                }
                void fetchSquads();
                void fetchPlayerCards();
              } catch (e: unknown) {
                const m =
                  (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                  'Failed to fill squad';
                message.error(m);
              }
            }}
          >
            <Button icon={<TeamOutlined />}>Fill Empty Slots</Button>
          </Popconfirm>
          <Popconfirm
            title="Skip FTUE?"
            icon={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
            description={
              <div style={{ maxWidth: 360 }}>
                <p style={{ marginBottom: 8 }}>
                  Fills the home XI + 4 subs using the user&apos;s existing unused cards first
                  (role-aware), then generates only the cards needed to fill the remaining slots.
                  Existing cards are preserved. Cannot be cleanly reverted.
                </p>
                <Alert
                  type="warning"
                  showIcon
                  message="Generation may break the client for this user (low chance)"
                  description="Newly generated cards bypass the client's normal FTUE flow. In rare cases this can leave the client in an inconsistent state. Use only when the player is genuinely stuck."
                />
              </div>
            }
            okText="Skip & Fill Squad"
            okButtonProps={{ danger: true }}
            onConfirm={async () => {
              try {
                const res = await adminApi.skipPlayerFtue(id!);
                const { previousStep, currentStep, assignedExisting, generated } = res.data;
                message.success(
                  `FTUE skipped: ${previousStep} → ${currentStep}. ` +
                    `Used ${assignedExisting} existing card${assignedExisting === 1 ? '' : 's'}, ` +
                    `generated ${generated} new.`
                );
                void fetchSquads();
                void fetchPlayerCards();
              } catch (e: unknown) {
                const m =
                  (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                  'Failed to skip FTUE';
                message.error(m);
              }
            }}
          >
            <Button danger>Skip FTUE</Button>
          </Popconfirm>
        </Space>
      </Card>

      {/* Detailed Info Tabs */}
      <Tabs
        items={[
          {
            key: 'overview',
            label: 'Overview',
            children: (
              <Card>
                <Descriptions column={2}>
                  <Descriptions.Item label="Display Name">
                    {player.displayName || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Platform">{player.platform || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Auth Provider">
                    {player.authProvider || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Is Guest">
                    {player.isGuest ? 'Yes' : 'No'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Has Linked Account">
                    {player.hasLinkedAccount ? 'Yes' : 'No'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Google Play ID">
                    {player.googlePlayId || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Apple Game Center ID">
                    {player.appleGameCenterId || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created">
                    {new Date(player.createdAt).toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Last Login">
                    {player.lastLoginAt ? new Date(player.lastLoginAt).toLocaleString() : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Client Build">
                    {/* x-build-number first; lastBuildVersion only for clients that send an integer x-build-version */}
                    {player.lastBuildNumber ?? player.lastBuildVersion ?? (
                      <Tooltip title="Recorded from the build number header on the player's last session (Android versionCode / iOS build). Blank means they have not been seen since this was added, or their client sends no build number.">
                        <Text type="secondary">Not recorded</Text>
                      </Tooltip>
                    )}
                    {player.lastAppVersion ? ` (${player.lastAppVersion})` : ''}
                    {player.lastClientPlatform ? ` · ${player.lastClientPlatform}` : ''}
                  </Descriptions.Item>
                  <Descriptions.Item label="Install Source">
                    <PlayerInstallSource source={player.installSource} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Config Version">
                    {player.lastConfigVersion ? (
                      <Tag color="blue">{player.lastConfigVersion}</Tag>
                    ) : (
                      <Tooltip title="This build is not mapped to a config version, so the player receives the global configuration.">
                        <Text type="secondary">Global</Text>
                      </Tooltip>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Client Last Seen">
                    {player.lastClientSeenAt
                      ? new Date(player.lastClientSeenAt).toLocaleString()
                      : '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            ),
          },
          {
            key: 'economy',
            label: 'Economy',
            children: economy ? (
              <Row gutter={16}>
                <Col span={12}>
                  <Card title="Currency Balances">
                    <Descriptions column={1}>
                      <Descriptions.Item label="Coins">
                        {economy.coins?.toLocaleString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Evo Tokens">
                        {economy.evoTokens?.toLocaleString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Match Tokens">
                        {economy.matchTokens?.toLocaleString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Premium Currency">
                        {economy.premiumCurrency?.toLocaleString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Fan Rating">
                        {economy.fanRating?.toLocaleString()}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Statistics">
                    <Descriptions column={1}>
                      <Descriptions.Item label="Level">{economy.level}</Descriptions.Item>
                      <Descriptions.Item label="Total XP">
                        {economy.totalXp?.toLocaleString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Matches Played">
                        {economy.matchesPlayed}
                      </Descriptions.Item>
                      <Descriptions.Item label="Matches Won">
                        {economy.matchesWon}
                      </Descriptions.Item>
                      <Descriptions.Item label="Win Rate">
                        {economy.matchesPlayed > 0
                          ? ((economy.matchesWon / economy.matchesPlayed) * 100).toFixed(1) + '%'
                          : 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Consecutive Login Days">
                        {economy.consecutiveLoginDays}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
            ) : (
              <Card>No economy data available</Card>
            ),
          },
          {
            key: 'transactions',
            label: (
              <span>
                <HistoryOutlined /> Transactions
                {txMeta.total > 0 && <Tag style={{ marginLeft: 8 }}>{txMeta.total}</Tag>}
              </span>
            ),
            children: (
              <Card>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={5}>
                    <Select
                      placeholder="Currency Type"
                      allowClear
                      style={{ width: '100%' }}
                      onChange={(val) => {
                        const f = { ...txFilters, currencyType: val, page: 1 };
                        setTxFilters(f);
                        void fetchTransactions(f);
                      }}
                      options={[
                        { label: 'Coins', value: 'coins' },
                        { label: 'Evo Tokens', value: 'evoTokens' },
                        { label: 'Match Tokens', value: 'matchTokens' },
                        { label: 'Premium', value: 'premiumCurrency' },
                      ]}
                    />
                  </Col>
                  <Col span={5}>
                    <Input
                      placeholder="Source filter..."
                      allowClear
                      onPressEnter={(e) => {
                        const val = (e.target as HTMLInputElement).value;
                        const f = { ...txFilters, source: val || undefined, page: 1 };
                        setTxFilters(f);
                        void fetchTransactions(f);
                      }}
                    />
                  </Col>
                  <Col span={4}>
                    <Select
                      placeholder="Sort by"
                      defaultValue="createdAt"
                      style={{ width: '100%' }}
                      onChange={(val) => {
                        const f = { ...txFilters, sortBy: val, page: 1 };
                        setTxFilters(f);
                        void fetchTransactions(f);
                      }}
                      options={[
                        { label: 'Date', value: 'createdAt' },
                        { label: 'Amount', value: 'amount' },
                        { label: 'Type', value: 'currencyType' },
                      ]}
                    />
                  </Col>
                  <Col span={3}>
                    <Select
                      defaultValue="DESC"
                      style={{ width: '100%' }}
                      onChange={(val) => {
                        const f = { ...txFilters, sortOrder: val, page: 1 };
                        setTxFilters(f);
                        void fetchTransactions(f);
                      }}
                      options={[
                        { label: 'Newest', value: 'DESC' },
                        { label: 'Oldest', value: 'ASC' },
                      ]}
                    />
                  </Col>
                  <Col span={3}>
                    <Button
                      onClick={() => {
                        setTxFilters({});
                        void fetchTransactions({});
                      }}
                    >
                      Clear
                    </Button>
                  </Col>
                </Row>
                <Table<CurrencyTransaction>
                  dataSource={transactions}
                  columns={transactionColumns}
                  rowKey="id"
                  loading={txLoading}
                  size="small"
                  scroll={{ x: 800 }}
                  pagination={{
                    current: txMeta.page,
                    total: txMeta.total,
                    pageSize: 50,
                    showTotal: (total) => `${total} transactions`,
                    showSizeChanger: false,
                    onChange: (page) => {
                      const f = { ...txFilters, page };
                      setTxFilters(f);
                      void fetchTransactions(f);
                    },
                  }}
                  expandable={{
                    expandedRowRender: (record: CurrencyTransaction) =>
                      record.metadata ? (
                        <pre style={{ fontSize: 12, margin: 0 }}>
                          {JSON.stringify(record.metadata, null, 2)}
                        </pre>
                      ) : (
                        <Text type="secondary">No metadata</Text>
                      ),
                    rowExpandable: (record: CurrencyTransaction) => !!record.metadata,
                  }}
                />
              </Card>
            ),
          },
          {
            key: 'purchases',
            label: (
              <span>
                <DollarOutlined /> Purchases
                {purchases.length > 0 && <Tag style={{ marginLeft: 8 }}>{purchases.length}</Tag>}
              </span>
            ),
            children: (
              <Card loading={purchasesLoading}>
                {purchaseStats && (
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={4}>
                      <Statistic title="Total Purchases" value={purchaseStats.totalPurchases} />
                    </Col>
                    <Col span={4}>
                      {/* Per-currency totals — preferred when backend
                          ships totalSpentByCurrency. Falls back to the
                          deprecated scalar (assumed INR) for older builds. */}
                      {Array.isArray(
                        (
                          purchaseStats as unknown as {
                            totalSpentByCurrency?: Array<{
                              currency: string;
                              amount: number;
                            }>;
                          }
                        ).totalSpentByCurrency
                      ) &&
                      (
                        purchaseStats as unknown as {
                          totalSpentByCurrency: Array<{
                            currency: string;
                            amount: number;
                          }>;
                        }
                      ).totalSpentByCurrency.length > 0 ? (
                        <div>
                          <div style={{ color: '#8c8c8c', fontSize: 14 }}>Total Spent</div>
                          {(
                            purchaseStats as unknown as {
                              totalSpentByCurrency: Array<{
                                currency: string;
                                amount: number;
                              }>;
                            }
                          ).totalSpentByCurrency.map((row) => (
                            <div
                              key={row.currency}
                              style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}
                            >
                              {formatMoney(row.amount, row.currency)}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Statistic
                          title="Total Spent"
                          value={formatMoney(Number(purchaseStats.totalSpent) || 0, 'INR')}
                        />
                      )}
                    </Col>
                    <Col span={4}>
                      <Statistic
                        title="Avg Purchase"
                        value={formatMoney(Number(purchaseStats.averagePurchaseValue) || 0, 'INR')}
                      />
                    </Col>
                    <Col span={4}>
                      <Statistic
                        title="First Purchase"
                        value={
                          purchaseStats.firstPurchaseDate
                            ? new Date(purchaseStats.firstPurchaseDate).toLocaleDateString()
                            : 'N/A'
                        }
                      />
                    </Col>
                    <Col span={4}>
                      <Statistic
                        title="Last Purchase"
                        value={
                          purchaseStats.lastPurchaseDate
                            ? new Date(purchaseStats.lastPurchaseDate).toLocaleDateString()
                            : 'N/A'
                        }
                      />
                    </Col>
                    <Col span={4}>
                      <Button type="primary" onClick={() => setGrantPurchaseModalVisible(true)}>
                        <PlusOutlined /> Grant Purchase
                      </Button>
                    </Col>
                  </Row>
                )}
                {!purchaseStats && (
                  <div style={{ marginBottom: 16 }}>
                    <Button type="primary" onClick={() => setGrantModalVisible(true)}>
                      <PlusOutlined /> Grant Purchase
                    </Button>
                  </div>
                )}
                <Table<Purchase>
                  dataSource={purchases}
                  rowKey="id"
                  size="small"
                  pagination={{ pageSize: 20, showTotal: (t) => `${t} purchases` }}
                  columns={[
                    {
                      title: 'Product',
                      dataIndex: 'productId',
                      key: 'productId',
                      width: 160,
                      render: (v: string) => <Text strong>{v}</Text>,
                    },
                    {
                      title: 'Order ID',
                      dataIndex: 'orderId',
                      key: 'orderId',
                      width: 200,
                      ellipsis: true,
                    },
                    {
                      title: 'Platform',
                      dataIndex: 'platform',
                      key: 'platform',
                      width: 100,
                      render: (v: string) => (
                        <Tag
                          color={
                            v === 'google_play' ? 'green' : v === 'app_store' ? 'blue' : 'default'
                          }
                        >
                          {v}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Amount',
                      dataIndex: 'amount',
                      key: 'amount',
                      width: 130,
                      render: (_: unknown, record: Purchase) => {
                        if (record.amount == null) return '-';
                        const amt = Number(record.amount);
                        const cur = (record.currency || '').toUpperCase();
                        const COLORS: Record<string, string> = {
                          INR: '#3f8600',
                          USD: '#3f8600',
                          EUR: '#3f8600',
                          GBP: '#3f8600',
                          COINS: '#faad14',
                          MATCHTOKENS: '#722ed1',
                          EVOTOKENS: '#13c2c2',
                          GEMS: '#eb2f96',
                          PREMIUMCURRENCY: '#eb2f96',
                        };
                        return (
                          <Text strong style={{ color: COLORS[cur] }}>
                            {formatMoney(amt, cur)}
                          </Text>
                        );
                      },
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      width: 100,
                      filters: [
                        { text: 'Completed', value: 'completed' },
                        { text: 'Pending', value: 'pending' },
                        { text: 'Failed', value: 'failed' },
                        { text: 'Refunded', value: 'refunded' },
                      ],
                      onFilter: (value: unknown, record: Purchase) => record.status === value,
                      render: (s: string) => {
                        const colors: Record<string, string> = {
                          completed: 'green',
                          pending: 'orange',
                          failed: 'red',
                          refunded: 'purple',
                          cancelled: 'default',
                        };
                        return <Tag color={colors[s] || 'default'}>{s.toUpperCase()}</Tag>;
                      },
                    },
                    {
                      title: 'Type',
                      dataIndex: 'purchaseClassification',
                      key: 'classification',
                      width: 100,
                      render: (v: string) => (v ? <Tag>{v}</Tag> : null),
                    },
                    {
                      title: 'Date',
                      dataIndex: 'createdAt',
                      key: 'createdAt',
                      width: 160,
                      sorter: (a: Purchase, b: Purchase) =>
                        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
                      defaultSortOrder: 'descend' as const,
                      render: (d: string) => new Date(d).toLocaleString(),
                    },
                    {
                      title: 'Action',
                      key: 'action',
                      width: 70,
                      render: (_: unknown, record: Purchase) => (
                        <Button
                          size="small"
                          type="link"
                          onClick={() => navigate(`/purchases/${record.id}`)}
                        >
                          View
                        </Button>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'banHistory',
            label: (
              <span>
                <HistoryOutlined /> Ban History
                {banHistory.length > 0 && <Tag style={{ marginLeft: 8 }}>{banHistory.length}</Tag>}
              </span>
            ),
            children: (
              <Card>
                {banHistory.length > 0 ? (
                  <Timeline
                    items={banHistory.map((ban) => ({
                      color:
                        ban.status === 'active'
                          ? 'red'
                          : ban.status === 'revoked'
                            ? 'green'
                            : 'gray',
                      children: (
                        <div>
                          <p>
                            <strong>{ban.banType.toUpperCase()}</strong> - {ban.reason}
                            <Tag
                              color={
                                ban.status === 'active'
                                  ? 'red'
                                  : ban.status === 'revoked'
                                    ? 'green'
                                    : 'default'
                              }
                              style={{ marginLeft: 8 }}
                            >
                              {ban.status.toUpperCase()}
                            </Tag>
                          </p>
                          {ban.reasonDetails && <p>{ban.reasonDetails}</p>}
                          <p>
                            <Text type="secondary">Banned by: {ban.bannedByAdminEmail}</Text>
                          </p>
                          <p>
                            <Text type="secondary">
                              Date: {new Date(ban.createdAt).toLocaleString()}
                            </Text>
                          </p>
                          {ban.durationDays && (
                            <p>
                              <Text type="secondary">Duration: {ban.durationDays} days</Text>
                            </p>
                          )}
                          {ban.revokedAt && (
                            <>
                              <p>
                                <Text type="secondary">Revoked by: {ban.revokedByAdminEmail}</Text>
                              </p>
                              <p>
                                <Text type="secondary">
                                  Revoked at: {new Date(ban.revokedAt).toLocaleString()}
                                </Text>
                              </p>
                              <p>
                                <Text type="secondary">
                                  Revocation reason: {ban.revocationReason}
                                </Text>
                              </p>
                            </>
                          )}
                        </div>
                      ),
                    }))}
                  />
                ) : (
                  <Text type="secondary">No ban history</Text>
                )}
              </Card>
            ),
          },
          {
            key: 'matchHistory',
            label: (
              <span>
                <TrophyOutlined /> Match History
                {matchHistory?.total ? (
                  <Tag style={{ marginLeft: 8 }}>{matchHistory.total}</Tag>
                ) : null}
              </span>
            ),
            children: (
              <Card
                loading={matchesLoading}
                extra={
                  <Space>
                    <ExportButton
                      options={[
                        {
                          key: 'user-ai-analytics',
                          label: 'AI Analytics',
                          exportFn: (fromDate?: string, toDate?: string) =>
                            adminApi.exportUserAiAnalytics(id!, fromDate, toDate),
                          filename: `ai_analytics_user_${id}`,
                        },
                      ]}
                      buttonText="Export AI Analytics"
                      single
                    />
                    <Button
                      type="primary"
                      icon={<RiseOutlined />}
                      onClick={() => setAiAnalyticsModalVisible(true)}
                    >
                      Overall AI Analytics
                    </Button>
                  </Space>
                }
              >
                {matchHistory?.stats && (
                  <>
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                      <Col span={4}>
                        <Statistic title="Total Matches" value={matchHistory.stats.totalMatches} />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Wins"
                          value={matchHistory.stats.wins}
                          valueStyle={{ color: '#3f8600' }}
                        />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Losses"
                          value={matchHistory.stats.losses}
                          valueStyle={{ color: '#cf1322' }}
                        />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Win Rate"
                          value={matchHistory.stats.winRate?.toFixed(1)}
                          suffix="%"
                        />
                      </Col>
                      <Col span={4}>
                        <Statistic title="Highest Score" value={matchHistory.stats.highestScore} />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Avg Score"
                          value={matchHistory.stats.averageScore?.toFixed(0)}
                        />
                      </Col>
                    </Row>
                    {/* §2.4 — Match-type split + most-used overs (computed from
                        recentMatches; for full lifetime split we'd need a new
                        backend endpoint). */}
                    {(() => {
                      const recent = matchHistory.recentMatches || [];
                      const quickPlay = recent.filter((m) => m.matchType === 'quick_play').length;
                      const custom = recent.filter((m) => m.matchType === 'custom_match').length;
                      const oversCounts: Record<number, number> = {};
                      for (const m of recent) {
                        if (m.matchType === 'custom_match') {
                          oversCounts[m.overs] = (oversCounts[m.overs] || 0) + 1;
                        }
                      }
                      const mostUsedOvers = Object.entries(oversCounts).sort(
                        (a, b) => b[1] - a[1]
                      )[0];
                      return (
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                          <Col span={6}>
                            <Statistic title="Quickplay (recent)" value={quickPlay} />
                          </Col>
                          <Col span={6}>
                            <Statistic title="Custom (recent)" value={custom} />
                          </Col>
                          <Col span={6}>
                            <Statistic
                              title="Most-used custom overs"
                              value={
                                mostUsedOvers ? `${mostUsedOvers[0]} (${mostUsedOvers[1]}x)` : '—'
                              }
                            />
                          </Col>
                          <Col span={6}>
                            <Statistic
                              title="Last login"
                              value={
                                player?.lastLoginAt
                                  ? new Date(player.lastLoginAt).toLocaleString()
                                  : '—'
                              }
                            />
                          </Col>
                        </Row>
                      );
                    })()}
                  </>
                )}
                <Table
                  dataSource={matchHistory?.recentMatches || []}
                  columns={matchColumns}
                  rowKey="id"
                  expandable={{
                    expandedRowRender: (record: PlayerMatch) => {
                      const a = record.aiAnalytics;
                      if (!a)
                        return (
                          <Typography.Text type="secondary">No analytics data</Typography.Text>
                        );
                      const diffLabels: Record<number, string> = {
                        0: 'Easy',
                        1: 'Medium',
                        2: 'Hard',
                        3: 'Expert',
                      };
                      const verdictLabels: Record<number, string> = {
                        0: 'Won',
                        1: 'Lost',
                        [-1]: 'Tie',
                        2: 'Abandoned',
                      };
                      const verdictColors: Record<number, string> = {
                        0: 'green',
                        1: 'red',
                        [-1]: 'orange',
                        2: 'default',
                      };
                      const aigrpLabels: Record<string, string> = {
                        '0': 'Strong (NZ, ENG, IND, PAK, AUS, RSA)',
                        '1': 'Mid (BAN, SL, WI, AFG, IRE, SCO)',
                        '2': 'Weak (Others)',
                        MS: 'My Squad opponent',
                      };
                      const mfbLabels: Record<number, string> = {
                        1: 'User Batted First',
                        0: 'User Bowled First',
                      };
                      const divisionLabels: Record<number, string> = {
                        1: 'Rookie III',
                        2: 'Rookie II',
                        3: 'Rookie I',
                        4: 'Semi-Pro III',
                        5: 'Semi-Pro II',
                        6: 'Semi-Pro I',
                        7: 'Pro III',
                        8: 'Pro II',
                        9: 'Pro I',
                        10: 'National III',
                        11: 'National II',
                        12: 'National I',
                        13: 'Continental III',
                        14: 'Continental II',
                        15: 'Continental I',
                        16: 'Global III',
                        17: 'Global II',
                        18: 'Global I',
                        19: 'Legendary III',
                        20: 'Legendary II',
                        21: 'Legendary I',
                      };
                      const tourLabels: Record<number, string> = {
                        0: 'Quick Play',
                        1: 'Custom Match',
                      };
                      const wicketLabels: Record<number, string> = {
                        1: 'Bowled',
                        2: 'LBW',
                        3: 'Caught',
                        4: 'Run Out',
                        5: 'Ct Behind',
                        6: 'Stumped',
                        7: 'C&B',
                        8: 'Slip Catch',
                      };
                      const renderWickets = (wk: string | null | undefined) => {
                        if (!wk) return '-';
                        const codes = wk.split('|').filter((s) => s !== '');
                        if (codes.length === 0) return '-';
                        const labels = codes.map((c) => wicketLabels[Number(c)] || c);
                        return `${codes.join('|')} (${labels.join(' | ')})`;
                      };
                      return (
                        <Descriptions size="small" column={4} bordered>
                          <Descriptions.Item label="AI Difficulty">
                            {a.aidiff != null
                              ? `${a.aidiff} (${diffLabels[a.aidiff] ?? 'Unknown'})`
                              : '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Verdict">
                            {a.verdict != null ? (
                              <Tag color={verdictColors[a.verdict] ?? 'default'}>
                                {a.verdict} ({verdictLabels[a.verdict] ?? 'Unknown'})
                              </Tag>
                            ) : (
                              '-'
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item label="Division">
                            {a.divisions != null
                              ? `${a.divisions} (${divisionLabels[a.divisions] ?? 'Unknown'})`
                              : '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Overs">{a.ovr ?? '-'}</Descriptions.Item>
                          <Descriptions.Item label="User Team">
                            {a.userteam || '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="AI Team">{a.aiteam || '-'}</Descriptions.Item>
                          <Descriptions.Item label="PTR (Player Team Rating)">
                            {a.ptr ?? '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="OTR (Opponent Team Rating)">
                            {a.otr ?? '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="User Score">
                            {a.userscr || '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="AI Score">{a.aiscr || '-'}</Descriptions.Item>
                          <Descriptions.Item label="User Balls">
                            {a.userballs || '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="AI Balls">{a.aiballs || '-'}</Descriptions.Item>
                          <Descriptions.Item label="User 4s">{a.userFour ?? '-'}</Descriptions.Item>
                          <Descriptions.Item label="User 6s">{a.userSix ?? '-'}</Descriptions.Item>
                          <Descriptions.Item label="AI 4s">{a.aiFour ?? '-'}</Descriptions.Item>
                          <Descriptions.Item label="AI 6s">{a.aiSix ?? '-'}</Descriptions.Item>
                          <Descriptions.Item label="AI Dot Balls">
                            {a.aidotball ?? '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="AI Group">
                            {a.aigrp != null
                              ? `${a.aigrp} (${aigrpLabels[a.aigrp] ?? 'Unknown'})`
                              : '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="User Wickets">
                            {renderWickets(a.wku)}
                          </Descriptions.Item>
                          <Descriptions.Item label="AI Wickets">
                            {renderWickets(a.wka)}
                          </Descriptions.Item>
                          <Descriptions.Item label="User Miss">
                            {a.usermiss || '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="AI Miss">{a.aimiss || '-'}</Descriptions.Item>
                          <Descriptions.Item label="User Run Rate">
                            {a.userRunrate != null ? Number(a.userRunrate).toFixed(2) : '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="AI Run Rate">
                            {a.aiRunrate != null ? Number(a.aiRunrate).toFixed(2) : '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="OS">{a.os ?? '-'}</Descriptions.Item>
                          <Descriptions.Item label="Build Ver">{a.bv ?? '-'}</Descriptions.Item>
                          <Descriptions.Item label="Me First Batting">
                            {a.mfb != null ? `${a.mfb} (${mfbLabels[a.mfb] ?? 'Unknown'})` : '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Tournament ID">
                            {a.tour != null ? `${a.tour} (${tourLabels[a.tour] ?? 'Other'})` : '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Auto-Play Bat">
                            {a.apbatuser ?? '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Auto-Play Bowl">
                            {a.apbowluser ?? '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Match ID" span={2}>
                            {a.matchId || record.id || '-'}
                          </Descriptions.Item>
                          {a.bybDataUser && (
                            <Descriptions.Item label="BYB User" span={4}>
                              {a.bybDataUser}
                            </Descriptions.Item>
                          )}
                          {a.bybDataAi && (
                            <Descriptions.Item label="BYB AI" span={4}>
                              {a.bybDataAi}
                            </Descriptions.Item>
                          )}
                        </Descriptions>
                      );
                    },
                    rowExpandable: (record: PlayerMatch) => !!record.aiAnalytics,
                  }}
                  pagination={{
                    current: matchPage,
                    pageSize: matchPageSize,
                    total: matchHistory?.total || 0,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} matches`,
                    onChange: (page, pageSize) => {
                      setMatchPage(page);
                      setMatchPageSize(pageSize);
                      fetchMatchHistory(page, pageSize);
                    },
                  }}
                  locale={{ emptyText: <Empty description="No matches found" /> }}
                />
              </Card>
            ),
          },
          {
            key: 'squads',
            label: (
              <span>
                <TeamOutlined /> Squads
                {squads?.totalPlayerCards ? (
                  <Tag style={{ marginLeft: 8 }}>{squads.totalPlayerCards} cards</Tag>
                ) : null}
              </span>
            ),
            children: (
              <Card loading={squadsLoading}>
                {squads ? (
                  <div>
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                      <Col span={8}>
                        <Statistic
                          title="Total Player Cards"
                          value={squads.totalPlayerCards || 0}
                        />
                      </Col>
                    </Row>
                    {squads.homeSquad && renderSquadCard(squads.homeSquad)}
                    {squads.awaySquad && renderSquadCard(squads.awaySquad)}
                    {!squads.homeSquad && !squads.awaySquad && (
                      <Empty description="No squads created" />
                    )}

                    {/* All Player Cards */}
                    <Collapse
                      style={{ marginTop: 16 }}
                      defaultActiveKey={[]}
                      items={[
                        {
                          key: 'all-cards',
                          label: (
                            <Space>
                              <Text strong>All Player Cards</Text>
                              {playerCards && <Tag color="blue">{playerCards.total} total</Tag>}
                              {cardsLoading && <Tag>Loading...</Tag>}
                            </Space>
                          ),
                          children:
                            playerCards && playerCards.cards.length > 0 ? (
                              <Table<PlayerCard>
                                dataSource={playerCards.cards}
                                rowKey="id"
                                size="small"
                                scroll={{ x: 1000 }}
                                pagination={{
                                  defaultPageSize: 20,
                                  pageSizeOptions: [10, 20, 50, 100],
                                  showSizeChanger: true,
                                  showTotal: (total) => `${total} cards`,
                                }}
                                columns={[
                                  {
                                    title: 'Player',
                                    key: 'name',
                                    width: 160,
                                    render: (_: unknown, c: PlayerCard) => (
                                      <Text strong>
                                        {c.firstName} {c.secondName}
                                      </Text>
                                    ),
                                  },
                                  {
                                    title: 'Rarity',
                                    dataIndex: 'rarity',
                                    key: 'rarity',
                                    width: 100,
                                    render: (r: number) => renderRarity(r),
                                    filters: [
                                      { text: 'Common', value: 0 },
                                      { text: 'Rare', value: 1 },
                                      { text: 'Epic', value: 2 },
                                      { text: 'Legendary', value: 3 },
                                    ],
                                    onFilter: (value: unknown, record: PlayerCard) =>
                                      record.rarity === value,
                                  },
                                  {
                                    title: 'Rating',
                                    dataIndex: 'overallRating',
                                    key: 'overallRating',
                                    width: 70,
                                    sorter: (a: PlayerCard, b: PlayerCard) =>
                                      a.overallRating - b.overallRating,
                                  },
                                  {
                                    title: 'Lvl',
                                    dataIndex: 'playerLevel',
                                    key: 'playerLevel',
                                    width: 50,
                                  },
                                  {
                                    title: 'Evo',
                                    dataIndex: 'evolutionLevel',
                                    key: 'evolutionLevel',
                                    width: 50,
                                  },
                                  {
                                    title: 'Type',
                                    dataIndex: 'playerType',
                                    key: 'playerType',
                                    width: 100,
                                    render: (t: number) => typeLabels[t] || '-',
                                    filters: [
                                      { text: 'Batsman', value: 0 },
                                      { text: 'Bowler', value: 1 },
                                      { text: 'All-Rounder', value: 2 },
                                      { text: 'WK-Batsman', value: 3 },
                                    ],
                                    onFilter: (value: unknown, record: PlayerCard) =>
                                      record.playerType === value,
                                  },
                                  {
                                    title: 'BAT',
                                    dataIndex: 'battingAggregate',
                                    key: 'bat',
                                    width: 50,
                                  },
                                  {
                                    title: 'BWL',
                                    dataIndex: 'bowlingAggregate',
                                    key: 'bwl',
                                    width: 50,
                                  },
                                  {
                                    title: 'FLD',
                                    dataIndex: 'fieldingAggregate',
                                    key: 'fld',
                                    width: 50,
                                  },
                                  {
                                    title: 'PHY',
                                    dataIndex: 'physicalAggregate',
                                    key: 'phy',
                                    width: 50,
                                  },
                                  {
                                    title: 'WK',
                                    dataIndex: 'isWicketKeeper',
                                    key: 'wk',
                                    width: 50,
                                    render: (v: boolean) => (v ? <Tag color="cyan">WK</Tag> : null),
                                  },
                                  {
                                    title: 'Squad',
                                    dataIndex: 'isInSquad',
                                    key: 'isInSquad',
                                    width: 70,
                                    render: (v: boolean) => (
                                      <Tag color={v ? 'green' : 'default'}>{v ? 'Yes' : 'No'}</Tag>
                                    ),
                                  },
                                  {
                                    title: 'Locked',
                                    dataIndex: 'isLocked',
                                    key: 'isLocked',
                                    width: 70,
                                    render: (v: boolean) =>
                                      v ? <Tag color="orange">Locked</Tag> : null,
                                  },
                                  {
                                    title: 'Matches',
                                    dataIndex: 'matchesPlayed',
                                    key: 'matchesPlayed',
                                    width: 70,
                                    sorter: (a: PlayerCard, b: PlayerCard) =>
                                      (a.matchesPlayed ?? 0) - (b.matchesPlayed ?? 0),
                                  },
                                  {
                                    title: 'Actions',
                                    key: 'actions',
                                    width: 90,
                                    fixed: 'right' as const,
                                    render: (_: unknown, card: PlayerCard) => {
                                      const inSquad = !!card.isInSquad;
                                      return (
                                        <Popconfirm
                                          title="Delete this player card?"
                                          description="This permanently removes the card from the user's collection. The card cannot be in any squad."
                                          okText="Delete"
                                          okButtonProps={{ danger: true }}
                                          cancelText="Cancel"
                                          disabled={inSquad}
                                          onConfirm={async () => {
                                            try {
                                              await adminApi.deletePlayerCard(id!, card.id);
                                              message.success('Card deleted');
                                              fetchPlayerCards();
                                            } catch (e: unknown) {
                                              const m =
                                                (
                                                  e as {
                                                    response?: { data?: { message?: string } };
                                                  }
                                                )?.response?.data?.message ||
                                                'Failed to delete card';
                                              message.error(m);
                                            }
                                          }}
                                        >
                                          <Button
                                            danger
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            disabled={inSquad}
                                            title={
                                              inSquad
                                                ? 'Card is in a squad — clear the slot first'
                                                : 'Delete this card'
                                            }
                                          >
                                            Delete
                                          </Button>
                                        </Popconfirm>
                                      );
                                    },
                                  },
                                ]}
                                expandable={{
                                  expandedRowRender: (card: PlayerCard) => (
                                    <Row gutter={24}>
                                      <Col span={12}>
                                        <Descriptions
                                          title="Batting Stats"
                                          size="small"
                                          column={2}
                                          bordered
                                        >
                                          <Descriptions.Item label="Runs">
                                            {card.battingStats?.totalRuns ?? 0}
                                          </Descriptions.Item>
                                          <Descriptions.Item label="Balls Faced">
                                            {card.battingStats?.totalBallsFaced ?? 0}
                                          </Descriptions.Item>
                                          <Descriptions.Item label="4s">
                                            {card.battingStats?.totalFours ?? 0}
                                          </Descriptions.Item>
                                          <Descriptions.Item label="6s">
                                            {card.battingStats?.totalSixes ?? 0}
                                          </Descriptions.Item>
                                          <Descriptions.Item label="50s">
                                            {card.battingStats?.halfCenturies ?? 0}
                                          </Descriptions.Item>
                                          <Descriptions.Item label="100s">
                                            {card.battingStats?.centuries ?? 0}
                                          </Descriptions.Item>
                                          <Descriptions.Item label="Best Score">
                                            {card.battingStats?.bestScore ?? 0}
                                          </Descriptions.Item>
                                          <Descriptions.Item label="Innings">
                                            {card.battingStats?.inningsPlayed ?? 0}
                                          </Descriptions.Item>
                                        </Descriptions>
                                      </Col>
                                      <Col span={12}>
                                        <Descriptions
                                          title="Bowling Stats"
                                          size="small"
                                          column={2}
                                          bordered
                                        >
                                          <Descriptions.Item label="Wickets">
                                            {card.bowlingStats?.totalWickets ?? 0}
                                          </Descriptions.Item>
                                          <Descriptions.Item label="Balls Bowled">
                                            {card.bowlingStats?.totalBallsBowled ?? 0}
                                          </Descriptions.Item>
                                          <Descriptions.Item label="Runs Given">
                                            {card.bowlingStats?.totalRunsGiven ?? 0}
                                          </Descriptions.Item>
                                          <Descriptions.Item label="Maidens">
                                            {card.bowlingStats?.totalMaidens ?? 0}
                                          </Descriptions.Item>
                                          <Descriptions.Item label="Best Bowling">
                                            {card.bowlingStats?.bestBowlingWickets ?? 0}W
                                          </Descriptions.Item>
                                          <Descriptions.Item label="5W Hauls">
                                            {card.bowlingStats?.fiveWicketHauls ?? 0}
                                          </Descriptions.Item>
                                        </Descriptions>
                                      </Col>
                                    </Row>
                                  ),
                                  rowExpandable: () => true,
                                }}
                              />
                            ) : (
                              <Empty description="No player cards" />
                            ),
                        },
                      ]}
                    />
                  </div>
                ) : (
                  <Empty description="No squad data available" />
                )}
              </Card>
            ),
          },
          {
            key: 'devices',
            label: (
              <span>
                <MobileOutlined /> Devices
                {devices && (
                  <Tag style={{ marginLeft: 8 }}>
                    {devices.activeDevices}/{devices.totalDevices}
                  </Tag>
                )}
              </span>
            ),
            children: (
              <Card loading={devicesLoading}>
                {devices && devices.devices.length > 0 ? (
                  <div>
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                      <Col span={8}>
                        <Statistic title="Total Devices" value={devices.totalDevices} />
                      </Col>
                      <Col span={8}>
                        <Statistic title="Active Devices" value={devices.activeDevices} />
                      </Col>
                    </Row>
                    <Table<PlayerDevice>
                      dataSource={devices.devices}
                      rowKey="id"
                      size="small"
                      pagination={false}
                      columns={[
                        {
                          title: 'Platform',
                          dataIndex: 'platform',
                          key: 'platform',
                          width: 100,
                          render: (platform: string) => (
                            <Tag color={platform === 'android' ? 'green' : 'blue'}>
                              {platform.toUpperCase()}
                            </Tag>
                          ),
                        },
                        {
                          title: 'Device Model',
                          dataIndex: 'deviceModel',
                          key: 'deviceModel',
                          width: 150,
                          render: (val: string) => val || '-',
                        },
                        {
                          title: 'OS Version',
                          dataIndex: 'osVersion',
                          key: 'osVersion',
                          width: 110,
                          render: (val: string) => val || '-',
                        },
                        {
                          title: 'App Version',
                          dataIndex: 'appVersion',
                          key: 'appVersion',
                          width: 110,
                          render: (val: string) => val || '-',
                        },
                        {
                          title: 'Status',
                          dataIndex: 'isActive',
                          key: 'isActive',
                          width: 90,
                          render: (isActive: boolean) => (
                            <Tag color={isActive ? 'success' : 'default'}>
                              {isActive ? 'Active' : 'Inactive'}
                            </Tag>
                          ),
                        },
                        {
                          title: 'Last Active',
                          dataIndex: 'lastActiveAt',
                          key: 'lastActiveAt',
                          width: 160,
                          render: (date: string) => (date ? new Date(date).toLocaleString() : '-'),
                        },
                        {
                          title: 'FCM Token',
                          dataIndex: 'fcmToken',
                          key: 'fcmToken',
                          ellipsis: true,
                          render: (token: string) => (
                            <Tooltip title={token}>
                              <Text copyable={{ text: token }} style={{ maxWidth: 200 }} ellipsis>
                                {token.substring(0, 20)}...
                              </Text>
                            </Tooltip>
                          ),
                        },
                        {
                          title: 'Registered',
                          dataIndex: 'createdAt',
                          key: 'createdAt',
                          width: 160,
                          render: (date: string) => new Date(date).toLocaleString(),
                        },
                      ]}
                    />
                  </div>
                ) : (
                  <Empty description="No devices registered" />
                )}
              </Card>
            ),
          },
          {
            key: 'transferHistory',
            label: (
              <span>
                <SwapOutlined /> Transfer History
                {transferHistory.length > 0 && (
                  <Tag style={{ marginLeft: 8 }}>{transferHistory.length}</Tag>
                )}
              </span>
            ),
            children: (
              <Card>
                {transferHistory.length > 0 ? (
                  <Timeline
                    items={transferHistory.map((transfer) => ({
                      color: 'blue',
                      children: (
                        <div>
                          <p>
                            <strong>
                              {transfer.fromProvider} → {transfer.toProvider}
                            </strong>
                          </p>
                          <p>
                            <Text type="secondary">Reason: {transfer.reason}</Text>
                          </p>
                          <p>
                            <Text type="secondary">By: {transfer.adminEmail}</Text>
                          </p>
                          <p>
                            <Text type="secondary">
                              Date: {new Date(transfer.createdAt).toLocaleString()}
                            </Text>
                          </p>
                        </div>
                      ),
                    }))}
                  />
                ) : (
                  <Text type="secondary">No transfer history</Text>
                )}
              </Card>
            ),
          },
          {
            key: 'matchdayDirector',
            label: (
              <span>
                <DashboardOutlined /> Matchday Director
              </span>
            ),
            children: (
              <Card
                loading={directorLoading}
                extra={
                  directorData ? (
                    <Space>
                      <Button
                        icon={<DashboardOutlined />}
                        onClick={() => {
                          editDirectorForm.setFieldsValue({
                            currentDifficulty: directorData.directorFactors.currentDifficulty,
                            suggestedDifficulty: directorData.directorFactors.suggestedDifficulty,
                            churnRiskScore: directorData.churnAssessment.churnRiskScore,
                            consecutiveLosses: directorData.profile.consecutiveLosses,
                            daysSinceLastMatch: directorData.profile.daysSinceLastMatch,
                            winRate: directorData.profile.winRate,
                            recentWinRate: directorData.profile.recentWinRate,
                          });
                          setEditDirectorModalVisible(true);
                        }}
                      >
                        Edit Director
                      </Button>
                    </Space>
                  ) : null
                }
              >
                {directorData ? (
                  <div>
                    {/* Director Factors */}
                    <Title level={5} style={{ marginBottom: 16 }}>
                      Director Factors
                    </Title>
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                      <Col span={4}>
                        <Statistic
                          title="Difficulty Factor (dF)"
                          value={directorData.directorFactors.difficultyFactor.toFixed(3)}
                          valueStyle={{
                            color:
                              directorData.directorFactors.difficultyLevel === 'very_hard'
                                ? '#820014'
                                : directorData.directorFactors.difficultyLevel === 'hard'
                                  ? '#cf1322'
                                  : directorData.directorFactors.difficultyLevel === 'medium'
                                    ? '#d48806'
                                    : directorData.directorFactors.difficultyLevel === 'easy'
                                      ? '#1890ff'
                                      : '#3f8600',
                          }}
                        />
                        <Tag
                          color={
                            directorData.directorFactors.difficultyModeLabel === 'expert'
                              ? 'magenta'
                              : directorData.directorFactors.difficultyModeLabel === 'hard'
                                ? 'red'
                                : directorData.directorFactors.difficultyModeLabel === 'medium'
                                  ? 'orange'
                                  : 'green'
                          }
                          style={{ marginTop: 4 }}
                        >
                          {directorData.directorFactors.difficultyModeLabel?.toUpperCase()}
                        </Tag>
                      </Col>
                      <Col span={4}>
                        <Tooltip title="wLF = 1 + ((winRate - 0.5) × 20) / 100">
                          <Statistic
                            title="Win-Loss Factor (wLF)"
                            value={directorData.directorFactors.winLossFactor.toFixed(4)}
                          />
                        </Tooltip>
                      </Col>
                      <Col span={4}>
                        <Tooltip title="cF = 0.5 × min(1, daysSinceLastMatch / 7)">
                          <Statistic
                            title="Churn Factor (cF)"
                            value={directorData.directorFactors.churnFactor.toFixed(4)}
                          />
                        </Tooltip>
                      </Col>
                      <Col span={4}>
                        <Tooltip title="cW = min(1, churnRiskScore / 100)">
                          <Statistic
                            title="Churn Weight (cW)"
                            value={directorData.directorFactors.churnWeight.toFixed(4)}
                          />
                        </Tooltip>
                      </Col>
                      <Col span={4}>
                        <Tooltip title="Stored difficulty (0-100). Updated after each match to match suggested difficulty.">
                          <Statistic
                            title="Current Difficulty"
                            value={directorData.directorFactors.currentDifficulty}
                            suffix="/ 100"
                          />
                        </Tooltip>
                      </Col>
                      <Col span={4}>
                        <Tooltip title="Recommended difficulty based on win rate and recent form. Applied to current difficulty after each match.">
                          <Statistic
                            title="Suggested Difficulty"
                            value={directorData.directorFactors.suggestedDifficulty}
                            suffix="/ 100"
                          />
                        </Tooltip>
                      </Col>
                      <Col span={4}>
                        <Tooltip title="Final difficulty (0-100) after applying adjustments for form, churn protection, losing streak, and division. This is what the opponent API uses.">
                          <Statistic
                            title="Target Difficulty"
                            value={directorData.directorFactors.targetDifficulty}
                            suffix="/ 100"
                          />
                        </Tooltip>
                      </Col>
                      <Col span={4}>
                        <Tooltip title="Difficulty sent to the game client: 0 EASY, 1 MEDIUM, 2 HARD, 3 EXPERT. Flag off: the target difficulty (with the division bonus) cut at < 35 / < 60 / < 80. With matchday_director_gdd_ranges on: the GDD tier of dF, no division bonus (default thresholds: < 0.9 easy, 0.9 to 1.2 medium, above 1.2 to 2 hard, above 2 expert).">
                          <Statistic
                            title="Client API Difficulty"
                            value={`${directorData.directorFactors.difficultyMode} (${directorData.directorFactors.difficultyModeLabel?.toUpperCase()})`}
                            suffix="/ 3"
                          />
                        </Tooltip>
                      </Col>
                    </Row>

                    {/* Player Performance (from match records) */}
                    <Title level={5} style={{ marginBottom: 16 }}>
                      Player Performance
                    </Title>
                    <Row gutter={16} style={{ marginBottom: 8 }}>
                      <Col span={4}>
                        <Statistic
                          title="Total Matches"
                          value={
                            directorData.matchTableStats?.totalMatches ??
                            directorData.profile.totalMatches
                          }
                        />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Wins"
                          value={directorData.matchTableStats?.wins ?? directorData.profile.wins}
                          valueStyle={{ color: '#3f8600' }}
                        />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Losses"
                          value={
                            directorData.matchTableStats?.losses ?? directorData.profile.losses
                          }
                          valueStyle={{ color: '#cf1322' }}
                        />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Win Rate"
                          value={(
                            (directorData.matchTableStats?.winRate ??
                              directorData.profile.winRate) * 100
                          ).toFixed(1)}
                          suffix="%"
                        />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Avg Run Rate"
                          value={directorData.profile.avgRunRate.toFixed(2)}
                        />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Avg Economy"
                          value={directorData.profile.avgEconomy.toFixed(2)}
                        />
                      </Col>
                    </Row>
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                      <Col span={4}>
                        <Statistic
                          title="Runs Scored"
                          value={directorData.profile.totalRunsScored}
                        />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Runs Conceded"
                          value={directorData.profile.totalRunsConceded}
                        />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Wickets Taken"
                          value={directorData.profile.totalWicketsTaken}
                        />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Wickets Lost"
                          value={directorData.profile.totalWicketsLost}
                        />
                      </Col>
                      <Col span={4}>
                        <Statistic
                          title="Recent Win Rate"
                          value={(directorData.profile.recentWinRate * 100).toFixed(1)}
                          suffix="%"
                        />
                      </Col>
                      <Col span={4}>
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Recent Form
                          </Text>
                          <div style={{ marginTop: 4 }}>
                            {directorData.profile.recentForm ? (
                              directorData.profile.recentForm.split('').map((c, i) => (
                                <Tag
                                  key={i}
                                  color={c === 'W' ? 'green' : 'red'}
                                  style={{ marginRight: 2, marginBottom: 2 }}
                                >
                                  {c}
                                </Tag>
                              ))
                            ) : (
                              <Text type="secondary">No data</Text>
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Opponent Adjustments */}
                    <Title level={5} style={{ marginBottom: 16 }}>
                      Opponent Adjustments
                    </Title>
                    <Descriptions bordered size="small" column={3} style={{ marginBottom: 24 }}>
                      <Descriptions.Item label="Run Rate Range">
                        [{directorData.opponentAdjustments.runRateRange[0].toFixed(2)},{' '}
                        {directorData.opponentAdjustments.runRateRange[1].toFixed(2)}]
                      </Descriptions.Item>
                      <Descriptions.Item label="Economy Range">
                        [{directorData.opponentAdjustments.economyRange[0].toFixed(2)},{' '}
                        {directorData.opponentAdjustments.economyRange[1].toFixed(2)}]
                      </Descriptions.Item>
                      <Descriptions.Item label="Team Rating Range (OTR vs PTR)">
                        [{directorData.opponentAdjustments.teamRatingRange[0]},{' '}
                        {directorData.opponentAdjustments.teamRatingRange[1]}]
                      </Descriptions.Item>
                    </Descriptions>

                    {/* Churn Risk */}
                    <Title level={5} style={{ marginBottom: 16 }}>
                      Churn Risk Assessment
                    </Title>
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                      <Col span={6}>
                        <Progress
                          type="dashboard"
                          percent={directorData.churnAssessment.churnRiskScore}
                          strokeColor={
                            directorData.churnAssessment.churnRiskScore > 70
                              ? '#cf1322'
                              : directorData.churnAssessment.churnRiskScore > 40
                                ? '#d48806'
                                : '#3f8600'
                          }
                          format={(percent) => `${percent}`}
                        />
                        <div style={{ textAlign: 'center', marginTop: 4 }}>
                          {directorData.churnAssessment.isChurnProtectionActive ? (
                            <Tag color="red">Protection Active</Tag>
                          ) : (
                            <Tag color="green">Normal</Tag>
                          )}
                        </div>
                      </Col>
                      <Col span={18}>
                        <Descriptions bordered size="small" column={2}>
                          <Descriptions.Item label="Consecutive Losses">
                            {directorData.profile.consecutiveLosses} (+
                            {directorData.churnAssessment.components.consecutiveLosses} risk)
                          </Descriptions.Item>
                          <Descriptions.Item label="Recent Win Rate">
                            {(directorData.profile.recentWinRate * 100).toFixed(1)}% (+
                            {directorData.churnAssessment.components.recentWinRate} risk)
                          </Descriptions.Item>
                          <Descriptions.Item label="Days Since Last Match">
                            {directorData.profile.daysSinceLastMatch} (+
                            {directorData.churnAssessment.components.inactivity} risk)
                          </Descriptions.Item>
                          <Descriptions.Item label="Session Frustration">
                            +{directorData.churnAssessment.components.sessionFrustration} risk
                          </Descriptions.Item>
                        </Descriptions>
                      </Col>
                    </Row>

                    {/* Session Stats */}
                    <Title level={5} style={{ marginBottom: 16 }}>
                      Session Stats
                    </Title>
                    <Descriptions bordered size="small" column={3}>
                      <Descriptions.Item label="Matches This Session">
                        {directorData.sessionStats.matchesThisSession}
                      </Descriptions.Item>
                      <Descriptions.Item label="Wins This Session">
                        {directorData.sessionStats.winsThisSession}
                      </Descriptions.Item>
                      <Descriptions.Item label="Session Started">
                        {directorData.sessionStats.sessionStartedAt
                          ? new Date(directorData.sessionStats.sessionStartedAt).toLocaleString()
                          : 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Total Session Time">
                        {Math.floor(directorData.sessionStats.totalSessionTime / 3600)}h{' '}
                        {Math.floor((directorData.sessionStats.totalSessionTime % 3600) / 60)}m
                      </Descriptions.Item>
                      <Descriptions.Item label="Session Count">
                        {directorData.sessionStats.sessionCount}
                      </Descriptions.Item>
                      <Descriptions.Item label="Last Updated">
                        {new Date(directorData.lastUpdated).toLocaleString()}
                      </Descriptions.Item>
                    </Descriptions>

                    {/* Global Average Session Time (gAST) */}
                    {directorData.gast && (
                      <>
                        <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
                          Global Average Session Time (gAST)
                        </Title>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                          <Col span={5}>
                            <Statistic
                              title="gAST"
                              value={
                                directorData.gast.value > 0
                                  ? `${Math.floor(directorData.gast.value / 3600)}h ${Math.floor((directorData.gast.value % 3600) / 60)}m`
                                  : 'N/A'
                              }
                            />
                            <Tag
                              color={directorData.gast.isOverride ? 'orange' : 'green'}
                              style={{ marginTop: 4 }}
                            >
                              {directorData.gast.isOverride ? 'Override' : 'Calculated'}
                            </Tag>
                          </Col>
                          <Col span={5}>
                            <Statistic
                              title="Player Avg Session"
                              value={
                                directorData.gast.playerAvgSessionTime > 0
                                  ? `${Math.floor(directorData.gast.playerAvgSessionTime / 3600)}h ${Math.floor((directorData.gast.playerAvgSessionTime % 3600) / 60)}m`
                                  : 'N/A'
                              }
                            />
                          </Col>
                          <Col span={5}>
                            <Tooltip title="GDD formula: cF = 1 - (pST / gAST)">
                              <Statistic
                                title="GDD Churn Factor"
                                value={directorData.gast.churnFactorGDD.toFixed(4)}
                              />
                            </Tooltip>
                          </Col>
                          <Col span={5}>
                            <Tooltip title="Current formula: cF = 0.5 * min(1, daysSinceLastMatch / 7)">
                              <Statistic
                                title="Current Churn Factor"
                                value={directorData.directorFactors.churnFactor.toFixed(4)}
                              />
                            </Tooltip>
                          </Col>
                          <Col span={4}>
                            <Button
                              size="small"
                              onClick={() => {
                                gastOverrideForm.setFieldsValue({
                                  value: directorData.gast?.isOverride
                                    ? directorData.gast.value
                                    : undefined,
                                });
                                setGastOverrideModalVisible(true);
                              }}
                              style={{ marginTop: 20 }}
                            >
                              Override gAST
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}

                    {/* Update Frequency */}
                    {directorData.updateFrequency && (
                      <>
                        <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
                          Variable Update Frequency
                        </Title>
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={[
                            {
                              key: 'wLF',
                              variable: 'Win-Loss Factor (wLF)',
                              schedule: directorData.updateFrequency.winLossFactor,
                            },
                            {
                              key: 'pST',
                              variable: 'Player Session Time (pST)',
                              schedule: directorData.updateFrequency.playerSessionTime,
                            },
                            {
                              key: 'gAST',
                              variable: 'Global Avg Session Time (gAST)',
                              schedule: directorData.updateFrequency.globalAvgSessionTime,
                            },
                            {
                              key: 'cF',
                              variable: 'Churn Factor (cF)',
                              schedule: directorData.updateFrequency.churnFactor,
                            },
                            {
                              key: 'cW',
                              variable: 'Churn Weight (cW)',
                              schedule: directorData.updateFrequency.churnWeight,
                            },
                            {
                              key: 'nD',
                              variable: 'Days Since Last Login (nD)',
                              schedule: directorData.updateFrequency.daysSinceLastLogin,
                            },
                            {
                              key: 'bF',
                              variable: 'Base Factor (bF)',
                              schedule: directorData.updateFrequency.baseFactor,
                            },
                            {
                              key: 'dayF',
                              variable: 'Day Factor (dayF)',
                              schedule: directorData.updateFrequency.dayFactor,
                            },
                          ]}
                          columns={[
                            {
                              title: 'Variable',
                              dataIndex: 'variable',
                              key: 'variable',
                            },
                            {
                              title: 'Update Schedule',
                              dataIndex: 'schedule',
                              key: 'schedule',
                              render: (text: string) => <Tag>{text}</Tag>,
                            },
                          ]}
                        />
                      </>
                    )}
                  </div>
                ) : (
                  <Empty description="No matchday director data available" />
                )}
              </Card>
            ),
          },
        ]}
      />

      {/* Ban Modal */}
      <Modal
        title="Ban Player"
        open={banModalVisible}
        onCancel={() => {
          setBanModalVisible(false);
          banForm.resetFields();
        }}
        footer={null}
      >
        <Form form={banForm} layout="vertical" onFinish={handleBanPlayer}>
          <Form.Item
            name="banType"
            label="Ban Type"
            rules={[{ required: true, message: 'Please select ban type' }]}
          >
            <Select>
              <Select.Option value="temporary">Temporary</Select.Option>
              <Select.Option value="permanent">Permanent</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.banType !== curr.banType}>
            {({ getFieldValue }) =>
              getFieldValue('banType') === 'temporary' && (
                <Form.Item
                  name="durationDays"
                  label="Duration (days)"
                  rules={[{ required: true, message: 'Please enter duration' }]}
                >
                  <InputNumber min={1} max={3650} style={{ width: '100%' }} />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Please select a reason' }]}
          >
            <Select>
              {BAN_REASONS.map((r) => (
                <Select.Option key={r.value} value={r.value}>
                  {r.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="reasonDetails" label="Details">
            <TextArea rows={3} placeholder="Additional details about the ban..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" danger htmlType="submit">
                Ban Player
              </Button>
              <Button onClick={() => setBanModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Unban Modal */}
      <Modal
        title="Unban Player"
        open={unbanModalVisible}
        onCancel={() => {
          setUnbanModalVisible(false);
          unbanForm.resetFields();
        }}
        footer={null}
      >
        <Form form={unbanForm} layout="vertical" onFinish={handleUnbanPlayer}>
          <Form.Item
            name="revocationReason"
            label="Reason for Unbanning"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <TextArea rows={3} placeholder="Why is this player being unbanned?" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Unban Player
              </Button>
              <Button onClick={() => setUnbanModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Grant Currency Modal */}
      <Modal
        title="Grant/Deduct Currency"
        open={grantModalVisible}
        onCancel={() => {
          setGrantModalVisible(false);
          grantForm.resetFields();
        }}
        footer={null}
      >
        <Form form={grantForm} layout="vertical" onFinish={handleGrantCurrency}>
          <Form.Item
            name="currencyType"
            label="Currency Type"
            rules={[{ required: true, message: 'Please select currency type' }]}
          >
            <Select>
              {CURRENCY_TYPES.map((c) => (
                <Select.Option key={c.value} value={c.value}>
                  {c.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="isDeduction" label="Operation" initialValue={false}>
            <Select>
              <Select.Option value={false}>
                <PlusOutlined style={{ color: 'green' }} /> Grant (Add)
              </Select.Option>
              <Select.Option value={true}>
                <MinusOutlined style={{ color: 'red' }} /> Deduct (Remove)
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true, message: 'Please enter amount' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <TextArea rows={2} placeholder="Reason for this change..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Confirm
              </Button>
              <Button onClick={() => setGrantModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Economy Modal */}
      <Modal
        title="Edit Economy"
        open={editEconomyModalVisible}
        onCancel={() => {
          setEditEconomyModalVisible(false);
          editEconomyForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={editEconomyForm} layout="vertical" onFinish={handleEditEconomy}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="coins" label="Coins">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="evoTokens" label="Evo Tokens">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="matchTokens" label="Match Tokens">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="premiumCurrency" label="Premium Currency">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="fanRating" label="Fan Rating">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.fanRating !== cur.fanRating}>
                {({ getFieldValue }) => {
                  const rating = getFieldValue('fanRating');
                  return rating != null ? (
                    <Text
                      type="secondary"
                      style={{ display: 'block', marginTop: -16, marginBottom: 8 }}
                    >
                      Division: <Tag color="blue">{getDivisionName(rating)}</Tag>
                    </Text>
                  ) : null;
                }}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Division Preset">
                <Select
                  placeholder="Set by division"
                  allowClear
                  onChange={(value: number) => {
                    if (value != null) editEconomyForm.setFieldValue('fanRating', value);
                  }}
                  showSearch
                  optionFilterProp="children"
                >
                  {DIVISION_PRESETS.map((d) => (
                    <Option key={d.minRating} value={d.minRating}>
                      {d.name} ({d.minRating.toLocaleString()})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="totalXp" label="Total XP">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="level" label="Level">
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <TextArea rows={2} placeholder="Reason for this change..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Save Changes
              </Button>
              <Button onClick={() => setEditEconomyModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Merge Account Modal */}
      <Modal
        title="Merge Account"
        open={mergeModalVisible}
        onCancel={() => {
          setMergeModalVisible(false);
          mergeForm.resetFields();
        }}
        footer={null}
        width={560}
      >
        <Alert
          message="Account Merge (irreversible)"
          description="Moves ALL data from a duplicate/mis-bound account (Loser) INTO this account (Survivor), then soft-retires the loser. Use to recover accounts split by a provider-id change (e.g. Play Games v1→v2). Verify both IDs first."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={mergeForm} layout="vertical" onFinish={handleMergeAccount}>
          <Form.Item label="Survivor (this account — kept)">
            <Input value={id} disabled />
          </Form.Item>

          <Form.Item
            name="loserUserId"
            label="Loser account ID (merged in & retired)"
            rules={[
              { required: true, message: 'Enter the duplicate account ID' },
              {
                pattern: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
                message: 'Must be a valid UUID',
              },
            ]}
          >
            <Input placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000" />
          </Form.Item>

          <Form.Item name="reason" label="Reason (recorded in audit log)">
            <Input.TextArea rows={2} placeholder="e.g. v1→v2 Play Games id split recovery" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button
              onClick={() => {
                setMergeModalVisible(false);
                mergeForm.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button type="primary" danger htmlType="submit" loading={mergeLoading}>
              Merge
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Transfer Account Modal */}
      <Modal
        title="Transfer Account"
        open={transferModalVisible}
        onCancel={() => {
          setTransferModalVisible(false);
          transferForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Alert
          message="Account Transfer Warning"
          description="This action will change the player's authentication provider and platform identifiers. This is irreversible and should only be done with proper authorization."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={transferForm} layout="vertical" onFinish={handleTransferAccount}>
          <Form.Item label="Current Account Info">
            <Descriptions size="small" bordered column={1}>
              <Descriptions.Item label="Current Provider">
                {player?.authProvider || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Platform">{player?.platform || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Google Play ID">
                {player?.googlePlayId || 'None'}
              </Descriptions.Item>
              <Descriptions.Item label="Apple Game Center ID">
                {player?.appleGameCenterId || 'None'}
              </Descriptions.Item>
            </Descriptions>
          </Form.Item>

          <Form.Item
            name="targetProvider"
            label="Transfer To"
            rules={[{ required: true, message: 'Please select target provider' }]}
          >
            <Select placeholder="Select target authentication provider">
              {AUTH_PROVIDERS.map((p) => (
                <Select.Option key={p.value} value={p.value}>
                  {p.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.targetProvider !== curr.targetProvider}
          >
            {({ getFieldValue }) => {
              const targetProvider = getFieldValue('targetProvider');

              if (targetProvider === 'guest') {
                return (
                  <Form.Item
                    name="deviceId"
                    label="New Device ID"
                    rules={[{ required: true, message: 'Please enter device ID' }]}
                  >
                    <Input placeholder="Enter the new device ID" />
                  </Form.Item>
                );
              }

              if (targetProvider === 'google_play_games') {
                return (
                  <Form.Item
                    name="googlePlayGamesId"
                    label="Google Play Games ID"
                    rules={[{ required: true, message: 'Please enter Google Play Games ID' }]}
                  >
                    <Input placeholder="Enter Google Play Games ID" />
                  </Form.Item>
                );
              }

              if (targetProvider === 'apple_game_center') {
                return (
                  <Form.Item
                    name="appleGameCenterId"
                    label="Apple Game Center ID"
                    rules={[{ required: true, message: 'Please enter Apple Game Center ID' }]}
                  >
                    <Input placeholder="Enter Apple Game Center ID" />
                  </Form.Item>
                );
              }

              return null;
            }}
          </Form.Item>

          <Form.Item name="clearOldPlatformIds" valuePropName="checked" initialValue={false}>
            <Select defaultValue={false}>
              <Select.Option value={false}>Keep old platform IDs</Select.Option>
              <Select.Option value={true}>Clear old platform IDs</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="reason"
            label="Reason for Transfer"
            rules={[
              { required: true, message: 'Please provide a reason' },
              { min: 10, message: 'Reason must be at least 10 characters' },
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Provide a detailed reason for this account transfer (required for audit)..."
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={transferLoading}>
                Transfer Account
              </Button>
              <Button onClick={() => setTransferModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: 'red' }} />
            <span style={{ color: 'red' }}>DELETE ALL PLAYER DATA</span>
          </Space>
        }
        open={deleteModalVisible}
        onOk={handleDeletePlayer}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDeletePassword('');
        }}
        okText="Yes, Delete Everything"
        okButtonProps={{ danger: true, loading: deleteLoading }}
        cancelButtonProps={{ disabled: deleteLoading }}
      >
        <Alert
          message="DANGER: IRREVERSIBLE ACTION"
          description={
            <div>
              <p>
                You are about to <strong>permanently delete all data</strong> for player{' '}
                <strong>{player.displayName}</strong> ({player.id}).
              </p>
              <p>
                This includes all matches, cards, currency, progress, and account information. This
                cannot be undone.
              </p>
            </div>
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form layout="vertical">
          <Form.Item label="Confirm Admin Password" required>
            <Input.Password
              placeholder="Enter your password to confirm"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              onPressEnter={handleDeletePlayer}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Set Division Modal */}
      <Modal
        title="Set Division"
        open={setDivisionModalVisible}
        onCancel={() => {
          setSetDivisionModalVisible(false);
          setDivisionForm.resetFields();
        }}
        footer={null}
      >
        <Form form={setDivisionForm} layout="vertical" onFinish={handleSetDivision}>
          <Form.Item
            name="fanRating"
            label="Fan Rating"
            rules={[{ required: true, message: 'Please enter fan rating' }]}
            help="Rookie: 0-700 | Semi-Pro: 701-1300 | Pro: 1301-1900 | National: 1901-2500 | Continental: 2501-3100 | Global: 3101-3700 | Legendary: 3701+"
          >
            <InputNumber min={0} max={10000} style={{ width: '100%' }} placeholder="e.g. 2500" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Set Division
              </Button>
              <Button onClick={() => setSetDivisionModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Director Modal */}
      <Modal
        title="Edit Matchday Director Variables"
        open={editDirectorModalVisible}
        onCancel={() => {
          setEditDirectorModalVisible(false);
          editDirectorForm.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Alert
          message="Warning"
          description="Changes will immediately affect matchmaking difficulty. Computed values (wLF, cF, cW, dF) will be recalculated from the new stored values."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={editDirectorForm} layout="vertical" onFinish={handleEditDirector}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="currentDifficulty" label="Current Difficulty (0-100)">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="suggestedDifficulty" label="Suggested Difficulty (0-100)">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="churnRiskScore" label="Churn Risk Score (0-100)">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="consecutiveLosses" label="Consecutive Losses">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="daysSinceLastMatch" label="Days Since Last Match">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="winRate" label="Win Rate (0-1)">
                <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="recentWinRate" label="Recent Win Rate (0-1)">
                <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <TextArea rows={2} placeholder="Reason for override (audit trail)" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Save Changes
              </Button>
              <Button
                onClick={() => {
                  setEditDirectorModalVisible(false);
                  editDirectorForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* gAST Override Modal */}
      <Modal
        title="Override Global Average Session Time (gAST)"
        open={gastOverrideModalVisible}
        onCancel={() => {
          setGastOverrideModalVisible(false);
          gastOverrideForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={gastOverrideForm}
          layout="vertical"
          onFinish={(values) =>
            handleGastOverride({
              value: values.value ?? null,
              reason: values.reason,
            })
          }
        >
          <Form.Item
            name="value"
            label="gAST Value (seconds)"
            help="Leave empty and click Clear Override to use calculated value"
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 1800 for 30 minutes" />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <TextArea rows={2} placeholder="Reason for override" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Set Override
              </Button>
              <Button
                danger
                onClick={() => {
                  const reason = gastOverrideForm.getFieldValue('reason');
                  if (!reason) {
                    message.warning('Please provide a reason first');
                    return;
                  }
                  handleGastOverride({ value: null, reason });
                }}
              >
                Clear Override
              </Button>
              <Button
                onClick={() => {
                  setGastOverrideModalVisible(false);
                  gastOverrideForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* AI Analytics Modal */}
      <Modal
        title="Overall AI Analytics"
        open={aiAnalyticsModalVisible}
        onCancel={() => setAiAnalyticsModalVisible(false)}
        footer={null}
        width="85%"
        centered
        destroyOnHidden
      >
        <PlayerAiAnalytics playerId={id!} />
      </Modal>

      {/* Grant Purchase Modal */}
      <Modal
        title="Grant Purchase to Player"
        open={grantPurchaseModalVisible}
        onCancel={() => {
          setGrantPurchaseModalVisible(false);
          grantPurchaseForm.resetFields();
        }}
        onOk={() => grantPurchaseForm.submit()}
        okText="Grant Purchase"
        afterOpenChange={(open) => {
          if (open && productList.length === 0) void loadProducts();
        }}
      >
        <Form form={grantPurchaseForm} layout="vertical" onFinish={handleGrantPurchase}>
          <Form.Item
            name="productId"
            label="Product"
            rules={[{ required: true, message: 'Select a product' }]}
          >
            <Select
              showSearch
              placeholder="Select product to grant"
              optionFilterProp="label"
              options={productList.map((p) => ({
                value: p.storeProductId,
                label: `${p.name} (${p.storeProductId}) [${p.type}]`,
              }))}
            />
          </Form.Item>
          <Form.Item name="orderId" label="Order ID (optional)">
            <Input placeholder="Auto-generated if empty" />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Enter reason for granting' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="e.g. Game failed to deliver IAP items, user reported issue #123"
            />
          </Form.Item>
        </Form>
      </Modal>

      {editingSlot && (
        <SquadSlotEditor
          open={!!editingSlot}
          onClose={() => setEditingSlot(null)}
          userId={id!}
          squadType={editingSlot.squadType}
          position={editingSlot.position}
          currentSlot={editingSlot.slot}
          allCards={playerCards?.cards || []}
          onAssigned={() => {
            void fetchSquads();
            void fetchPlayerCards();
          }}
        />
      )}
    </div>
  );
};
